/* eslint-disable import/no-named-as-default-member */
import Vue from 'vue'
import _ from 'lodash'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
// drschoice/flow#615: client documents are served by the API now; this base
// addresses public brand assets only (see util/assets.js).
import { DEFAULT_ASSETS_BASE } from '~/util/assets'
import { carriers } from '~/util/carriers'
import { fromDob } from '~/util/date'
import { getActiveBreakpoint } from '~/util/mediaQuery'
import { CHECKUP_WIZARD } from '~/data/checkup-config'
import { money, toMoneyNumber } from '~/util/string'
import { carrierCompanyName, carrierDisplayName, planDisplayName } from '~/util/plan-name'
import * as aep from '~/util/aep-recommendation'
import { newProgressId, pollProgress } from '~/util/quote-progress'
import { guessCurrentPlans, LINES_BY_COVERAGE } from '~/util/aep-current-plans'
import { currentPlanIds } from '~/util/summary-plans'
import { tablePeriod } from '~/util/drug-table'
import { cmsDeductibleTiers, deductibleTiersText, resolveDeductibleTiers } from '~/util/deductible-tiers'
import { isOpenStateClient } from '~/util/open-states'
import { isAttachmentExcludedBy } from '~/util/files'
import { householdDiscount } from '~/util/household-discount'
import CommonMixin from '~/mixins/common'
import CardMixin from '~/mixins/card'
import { jsonSafeParse, jsonSafeStringify } from '~/plugins/extras'

// const BENEFIT_COST_SHARE_RE = /(\$(?<dollars>[\d,]+(\.\d{2})?)|(?<percent>\d+)%|\$(?<mindollars>[\d,]+)-(?<maxdollars>[\d,]+)((\.\d{2})?)|(?<minpercent>\d+)-(?<maxpercent>\d+)%)( (copay|coinsurance))?/
const BENEFIT_COST_SHARE_RE = /(\$(?<mindollars>[\d,]+)-(?<maxdollars>[\d,]+)((\.\d{2})?)|(?<minpercent>\d+)-(?<maxpercent>\d+)%|\$(?<dollars>[\d,]+(\.\d{2})?)|\$(?<indollars>[\d,]+(\.\d{2})?\s*In-?network)|(?<percent>\d+)%)( (copay|coinsurance))?/i
const IN_NETWORK_BENEFIT_COST_SHARE_RE = /\$(?<indollars>[\d,]+(\.\d{2})?\s*In-?network)/i

const BENEFITS_CATEGORIES_ORDER = [
    'DEDUCTIBLE',
    'DOCTOR_VISITS',
    'HOSPITAL', // <-- new category
    'DIAGNOSTIC_PROCEDURES',
    // todo: @john, combined '__AMBULANCE' service
    'EMERGENCY_CARE',
    'MEDICARE_PART_B',
    // todo: @john, combined '__MENTAL_HEALTH' service, renamed it to General
    'MENTAL_HEALTH',
    'REHABILITATION_SERVICES',
    'MEDICAL_EQUIPMENT',
    'FOOT_CARE',

    'OUT_OF_POCKET', // <-- new category
    'VISION',
    'HEARING',
    'BASIC_DENTAL', // <--renamed 'PREVENTIVE_DENTAL'
    'COMPREHENSIVE_DENTAL',

    'DISCOUNT',

    // todo: @john, you didn't mention these?
    'OTHER',
]

// Both replacement tables and the display-name rules now live in
// util/plan-name.js, so the client-facing AEP wizard (which bypasses this
// store's parsePlan pipeline entirely) applies the same ones. `preferred_name`
// overrides still win over the regex tables -- see planDisplayName.

const BENEFITS_CATEGORIES_STEPS_MAP = (() => {
    const mapd = {
        name: 'This is the advantage insurance company.',
        premium: 'This is the monthly premium for your advantage plan.',
        type: 'This plan is considered advantage also known as MAPD',
        carrier: 'This is the name of the insurance company of the advantage plan',
    }
    const ma = {
        name: 'This is the advantage insurance company.',
        premium: 'This is the monthly premium for your advantage plan.',
        type: 'This plan is considered advantage with no-rx also known as MA',
        carrier: 'This is the name of the insurance company of the advantage plan',
    }
    const supp = {
        name: 'This is the supplemental insurance company. You can get your supplement and prescription plan from different companies (and you can mix and match).',
        premium: 'This is the monthly premium for your supplement plan',
        type: 'This plan is considered supplement plan also known as SUPP',
        carrier: 'This is the name of the insurance company of the supplemental plan',
    }
    const pdp = {
        name: 'This is the drug insurance company. You can get your supplement and prescription plan from different companies (and you can mix and match).',
        premium: 'This is the monthly premium for your drug plan',
        type: 'This is a stand alone Part D prescription plan, also known as PDP, that only includes coverage for Prescriptions',
        carrier: 'This is the name of the insurance company of the drug plan',
    }
    const gp = {
        name: 'This is the employer plan insurance company.',
        premium: 'This is the monthly premium for your employer plan',
        type: 'This plan is an employer/group plan also known as GP',
        carrier: 'This is the name of the insurance company of the employer plan',
    }
    const copaysSentence = 'These are the co-pays after you pay your deductible (if you have one)'
    const common = {
        yearly_premium: 'We just took your monthly premium and multiplied it by 12 to help you budget annually',
        full_id: 'These aren\'t as important to help you make a decision but rather serve as "barcodes" to help us identify these plans',
        referral_required: 'This is if the plan requires a specialists referral or not.',
        fee_amount: 'This is a one time fee.',
        drug_info: `
            <div class="text-left">
                This is a breakdown of how each of your medications is covered by the plan.
                <ul>
                    <li>
                        <strong>Tier:</strong> How much you pay for your medications is determined by the tier. Typically the higher the tier, the higher the price.
                    </li>
                    <li>
                        <strong>On Formulary:</strong> This means the medication is covered by the plan.
                    </li>
                    <li>
                        <strong>Prior Authorization:</strong> This means you will need further approval from your provider before the plan will cover this medication.
                    </li>
                    <li>
                        <strong>Quantity Limit/Amount/Days:</strong> This means the plan may have limits on the amount of the medication that they will cover within a certain time period.
                    </li>
                    <li>
                        <strong>Step Therapy:</strong> This means the plan may require that you try a generic medication before they will cover the medication you entered.
                    </li>
                </ul>
            </div>
        `,
        drugs_yearly_by_pharmacy: 'Drug costs shown vary based on the plan and the pharmacy that you use. Contact the plan if you have specific questions about drug costs.',
        drugs_monthly_by_pharmacy: 'Drug costs shown vary based the pharmacy and based on the month of the year. Contact the plan if you have specific questions about drug costs.',
        drugs_monthly_by_drug: 'Drug costs shown are based on your preferred or cheapest found pharmacy, split by month, or by quarter when every drug is a 90-day fill (mail order), taking into consideration deductibles, gap (gone after 2024) and catastrophic periods if any',
        byKey: {
            GENERAL: 'General plan essential information',
            DEDUCTIBLE: 'This is how much you have to pay out of pocket before insurance pays.',
            DEDUCTIBLE__DRUG_DEDUCTIBLE: 'You\'ll pay 100% of your drug costs until you reach this number. This is a per calendar year and not a per drug cost. This applies to drug tiers',

            HOSPITAL: 'These are your co-pays and deductibles that you have to pay if you\'re hospitalized',
            DOCTOR_VISITS: copaysSentence,
            DIAGNOSTIC_PROCEDURES: copaysSentence,
            EMERGENCY_CARE: copaysSentence,
            MEDICARE_PART_B: copaysSentence,
            MENTAL_HEALTH: copaysSentence,
            REHABILITATION_SERVICES: copaysSentence,
            MEDICAL_EQUIPMENT: copaysSentence,
            FOOT_CARE: copaysSentence,

            VISION: copaysSentence,
            HEARING: copaysSentence,
            BASIC_DENTAL: copaysSentence,
            COMPREHENSIVE_DENTAL: copaysSentence,

            OUT_OF_POCKET: 'This is the most you can spend any year.',
            DISCOUNT: 'Some plans give you a bigger discount if you have a spouse on the same plan or if you live with someone.',
        },
        tiers: 'This is how much you will pay for prescriptions depending on the tier at a preferred pharmacy.',
        providers: 'This will show you if your providers are in network, out of network or undetermined',
        pharmacies: `
            <div class="text-left">
                This is a specific breakdown of your drug costs based on the medications you entered and the pharmacy (or pharmacies) you've selected.
                <p class="mt-2">
                    <strong>In-network:</strong> This means you can fill your prescriptions at this pharmacy
                <br><br>
                    <strong>Preferred:</strong> This means that you'll get the lowest costs because your pharmacy is part of a preferred network
                </p>
            </div>
        `,
    }
    return {
        common,
        mapd,
        supp,
        pdp,
        gp,
        ma
    }
})()

const BENEFITS_CATEGORIES_IMGS_SRCS_MAP = {
    GENERAL: 'Diagnostics.png',
    DEDUCTIBLE: 'Medical Book.png',
    DEDUCTIBLE__DRUG_DEDUCTIBLE: 'Medical Book.png',
    DOCTOR_VISITS: 'Doctor.png',
    HOSPITAL: 'Hospital.png',
    DIAGNOSTIC_PROCEDURES: 'Radiation Theraphy.png',
    EMERGENCY_CARE: 'Ambulance.png',
    MEDICARE_PART_B: 'Blood Cells.png',
    MENTAL_HEALTH: 'Depression.png',
    REHABILITATION_SERVICES: 'Disabled.png',
    MEDICAL_EQUIPMENT: 'Injection.png',
    FOOT_CARE: 'Podiatry.png',
    VISION: 'Optometry.png',
    HEARING: 'Ear.png',
    BASIC_DENTAL: 'Dental.png',
    COMPREHENSIVE_DENTAL: 'Mouth.png',
    OUT_OF_POCKET: 'Price Tag.png',
    DISCOUNT: 'Savings.png',
    PROVIDERS: 'Doctor.png',
    DRUG_TIERS: 'Symptom List.png',
    DRUGS_INFO: 'Online Pharmacy.png',
    DRUGS_YEARLY_BY_PHARMACY: 'Pill Bottle.png',
    DRUGS_MONTHLY_BY_PHARMACY: 'Pill Bottle.png',
    DRUGS_MONTHLY_BY_DRUG: 'Pill Bottle.png',
    DRUGS_COSTS_PER_PHARMACY: 'Pill Bottle.png',
    RATINGS: 'Online Claim.png',
    OTHER: 'Ask A Doctor.png',
    FALLBACK: 'Medical Folder.png',
}

const SUPP_TYPES_NAMES = {
    MA_CORE: 'Core',
    MA_SUPP1: '1',
    MA_SUPP2: '1A',
    MA_SUPP_1A: '1A',
}

const PLAN_TYPE_LABELS = {
    mapd: 'Medicare Advantage Plan',
    supp: 'Medicare Supplement Plan',
    pdp: 'Prescription Drug Plan'
}

const DRUGS_ALTERNATIVES = [
    ['basaglar', 'lantus', 'levemir', 'toujeo', 'tresiba'],
    ['alfuzosin hydrochloride', 'doxazosin', 'tamsulosin hydrochloride'],
    ['alfuzosin', 'doxazosin', 'tamsulosin hydrochloride'],
    ['invokana', 'farxiga', 'jardiance'],
    ['invokamet', 'synjardy', 'xigduo'],
    ['kombiglyze', 'janumet', 'jentadueto'],
    ['movantik', 'lactulose', 'amitiza', 'relistor'],
    ['nexium', 'esomeprazole', 'lansoprazole', 'omeprazole', 'pantoprazole'],
    ['novolin', 'humulin'],
    ['novolog', 'humalog', 'insulin lispro'],
    ['onglyza', 'januvia', 'tradjenta'],
    ['oxycontin', 'xtampza'],
    ['pradaxa', 'eliquis', 'xarelto'],
    ['ventolin', 'proventil', 'albuterol', 'proair'],
    ['qvar', 'arnuity', 'flovent'],
    ['travatan', 'latanoprost', 'lumigan', 'travoprost'],
    ['trazodone hydrochloride', 'zolpidem tartrate', 'belsomra'],
    ['trazodone', 'zolpidem tartrate', 'belsomra'],
]
const DRUGS_ALTERNATIVES_MAP = _.keyBy(DRUGS_ALTERNATIVES, list => list[0])

const SCHEDULE_HELP_TEXT = {
    email: 'Once you submit your request, you\'ll be matched with one of our enrollment specialists who will reach out to you directly via email within one business day to provide personalized guidance and support. If you would like to speak with a specialist, please select phone or video.',
    email_only: 'Due to recent changes by insurance carriers, we are currently offering consultations by email only. Once you submit your request, you\'ll be matched with one of our enrollment specialists who will reach out to you directly via email within one business day to provide personalized guidance and support.',
    email_in_aep: 'Please note this option means you will be matched with one of our enrollment specialists. Once you confirm your request, the specialist will reach out to you directly via email to provide assistance/Medicare guidance within 1 business day. If you would like to speak with a Medicare specialist, please select phone.',
    email_only_in_aep: (state, getters) => `Unfortunately, due to recent changes from the insurance companies, we are not able to offer phone consultations this AEP. Once you confirm the AEP Survey request, our system will email you a unique link for you to securely provide us with your medications/providers/pharmacy/health information and our team will follow up with a recommendation via email ${getters.isBetweenSep1Oct12 ? 'by October 15th.' : 'within 3 business days.'}`,
    phone: 'After you select the date & time of your consultation, you will receive an email with a secure link to your pre-consultation survey. You will be asked to enter your provider, prescription, and pharmacy information. This will allow our team to conduct research prior to your consultation. Your enrollment specialist will call you on the exact date & time of your consultation.',
    phone_in_aep: 'Our phone consultations are currently booking 2-3 weeks out due to the Medicare annual enrollment period. For a faster recommendation, please request the AEP Survey option and we will follow up with a recommendation via email within 3 business days after you complete the survey.',
    video: 'Please note our video calls are hosted via Zoom. You do not need to download Zoom for this to work. At the date & time of your consultation, simply click on the link in your consultation confirmation email. You can join the video call via your web browser on a smartphone, tablet, computer, or laptop.<br><br>You will receive an email with a secure link to your pre-consultation survey. You will be asked to enter your provider, prescription, and pharmacy information. This will allow our team to conduct research prior to your consultation.',
    survey_in_aep: (state, getters) => `Once you confirm the AEP Survey request, our system will email you a unique link for you to securely provide us with your medications/providers/pharmacy/health information and our team will follow up with a recommendation via email ${getters.isBetweenSep1Oct12 ? 'by October 15th.' : 'within 3 business days.'}`,
    email_opt_in: 'By providing us with your email address, you are allowing Doctor\'s Choice to send you information via email. Your email address will not be shared with any third parties.',
    new_client_email: 'Since you haven\'t worked with us before, we would like to talk to you on a phone (or video) call, just this first time.',
    no_slots_note: 'Dear client, our phone/video consultation calendar is booking quickly for this annual enrollment period. If you are looking to review your existing Medicare coverage and you do not see any availability for an appointment, please schedule an email consultation and we will consult you via email.',
    no_contact: '⚠️ Unfortunately due to recent insurance carrier changes, Doctor\'s Choice is no longer able to provide services to individuals from non-participating employers. If you think this might be an error, or have questions, feel free to email us at <a href="mailto:help@doctorschoiceusa.com">help@doctorschoiceusa.com</a> — we\'re happy to help!'
}
SCHEDULE_HELP_TEXT.survey = SCHEDULE_HELP_TEXT.survey_in_aep

const DEFAULT_SUMMARY_DATA = {
    survey_updated: null,
    cards: {},
    plans: {},
    group_plans: {},
    config: {
        mode: null,
        aep: null,
        cards: [
            'intro',
            'recap'
        ],
        type: 'FULL',
    }
}

const CARRIER_PROVIDERS_SEARCH_LINKS = { /* todo */ }

export const state = () => {
    return {
        originalState: null,
        assetsBase: DEFAULT_ASSETS_BASE,
        user: null,
        group: null,
        brokerGroups: [],
        clients: [],
        crmClient: null,
        matchingClients: [],
        host_url: null,
        effective_host_url: null,
        // Set from window.STATE by the `state` mutation. Lets pages explain what a
        // demo instance deliberately doesn't do, rather than looking broken.
        demo: false,
        busy: false,
        year: null,
        years: {},
        lastDonutHoleYear: 2024,
        soa60days: null,
        // Whether this session has cleared identity verification. The
        // authoritative copy is the server session; this is a cache so every
        // page doesn't re-ask. See flow/accounts/identity.py.
        identityVerified: false,
        // Set by the /verify safety net in plugins/api.js once it has sent the
        // page to the checkpoint. The page's own error handling still runs
        // while the browser leaves, and pushAlert stays quiet for it. Never
        // cleared: if a beforeunload prompt cancels the navigation, alerts stay
        // off for that visit, and the next refused request redirects again.
        leavingForVerify: false,
        // Set when a selfserve client signs, consumed when they land on
        // /checkup/plans. In-memory on purpose: survey_completed_by_client_or_agent
        // is NOT idempotent (it posts @channel to #preconsults-surveys-queue on
        // every call), and `plans` is a nav destination a client can revisit —
        // so a flag that survived a reload would re-ping the channel each time.
        surveyAwaitingCompletion: false,
        quotes: [],
        unscrapedQuotes: [],
        quotesInSight: [],
        // #721 round 2: GET /api/consults/current-plans/{id}/ for the summary's
        // year; null until loaded or when the call failed.
        aepCurrentResolved: null,
        quotesCached: false,
        quotesLight: false,
        // flow#735: Oracle's progress snapshot while the Plans page's quote loads
        quoteProgress: null,
        planExtras: [],
        planNotes: [],
        planFiles: [],
        summaryFiles: [],
        summaryScriptsCards: [],
        sources: {},
        summaries: [],
        activityChoices: [],
        activities: [],
        commissions: null,
        magicUrl: null,
        summary: null,
        opportunities: [],
        activeProducts: [],
        alerts: [],
        comparing: {},
        surveys: {},
        survey: null,
        internalGroupName: 'Medicare Checkup',
        defaultSummaryData: DEFAULT_SUMMARY_DATA,
        drugsAlternativesMap: DRUGS_ALTERNATIVES_MAP,
        benefitsCategoriesStepsMap: BENEFITS_CATEGORIES_STEPS_MAP,
        benefitsCategoriesImgsSrcsMap: BENEFITS_CATEGORIES_IMGS_SRCS_MAP,
        home: {
            href: 'https://www.doctorschoiceusa.com',
            text: 'Doctor\'s Choice'
        },
        support: false,
        crmBase: 'https://doctorschoice.lightning.force.com',
        sidebar: true,
        activeBreakpoint: getActiveBreakpoint(),
        autosave: null,
        autosaveTimeout: null,
        usStates: [],
        county: null,
        groupPlans: [],
        userGroupPlans: [],
        highlightColor: '#ffc078',
    }
}

export const mutations = {
    updateQuoteNetworkProviders (state, { id, inNetworkProviders, outNetworkProviders }) {
        const quoteIndex = _.findIndex(state.quotes, p => p.id === id)
        if (quoteIndex > -1) {
            const quote = state.quotes[quoteIndex]
            quote.in_network_providers = inNetworkProviders
            quote.out_network_providers = outNetworkProviders
            Vue.set(state.quotes[quoteIndex], 'in_network_providers', inNetworkProviders)
            Vue.set(state.quotes[quoteIndex], 'out_network_providers', outNetworkProviders)
        }
    },
    updateQuotePrice (state, { id, price }) {
        const quoteIndex = _.findIndex(state.quotes, p => p.id === id)
        if (quoteIndex > -1) {
            const quote = state.quotes[quoteIndex]
            quote.direct_carrier_annual_premium = price
            Vue.set(state.quotes[quoteIndex], 'direct_carrier_annual_premium', price)
        }
    },
    state (state, STATE) {
        state.originalState = _.cloneDeep(STATE || null)
        if (STATE) {
            for (const k in STATE) {
                state[k] = STATE[k]
            }
            state.brokerGroups = STATE.broker_groups || []
        }
    },
    route (state, route) {
        state.route = _.pick(route || {}, ['query', 'params', 'path', 'fullPath'])
    },
    user (state, user) {
        state.user = user
    },
    edge (state, edge) {
        state.user.edge = !!edge
    },
    group (state, group) {
        if (group) {
            group.config = group.config || {}
        }
        state.group = group
    },
    clients (state, clients) {
        state.clients = clients
    },
    crmClient (state, crmClient) {
        state.crmClient = crmClient
    },
    matchingClients (state, clients) {
        state.matchingClients = clients
    },
    hostUrl (state, host) {
        state.host_url = host
    },
    effectiveHostUrl (state, host) {
        state.effective_host_url = host
    },
    busy (state, busy) {
        state.busy = busy
    },
    pushAlert (state, payload) {
        if (_.isNil(payload)) {
            return this.commit('clearAlerts')
        }
        // Whatever failed did so because the session is pending, and the page
        // is already on its way to /verify: an alert now only flashes an error
        // at a client who has done nothing wrong.
        if (state.leavingForVerify) {
            return
        }
        if (_.isString(payload)) {
            payload = Object.assign({}, { message: payload })
        }
        if (_.isObject(payload) && payload.message) {
            payload = Object.assign({}, { ...payload, message: payload.message })
        }
        if (!payload.key) {
            payload.key = `page-${uuidv4()}`
        }
        const alert = {
            dismissible: true,
            repeat: false,
            timeout: 10000,
            placement: this.getters.isSize('lte-md') ? 'bottom-full' : 'bottom-right',
            ...(payload || {})
        }
        const { timeout, variant, message, key } = alert
        if (message) {
            const sentryCaptureMessage = this.getters?.window?.$root?.sentryCaptureMessage
            if (sentryCaptureMessage && (variant === 'danger' || !variant)) {
                setTimeout(() => {
                    sentryCaptureMessage(message, {
                        contexts: {
                            extra: {
                                key,
                                variant,
                                timeout,
                                name: alert?.name,
                                stack: alert?.stack,
                                response: alert?.response,
                            }
                        },
                        tags: {
                            uialert: true,
                            variant: variant || 'danger'
                        },
                        screenshot: true
                    })
                }, 50)
            }

            if (!alert.repeat && _.find(state.alerts, a => a.message === alert.message)) {
                return
            }

            if (typeof window !== 'undefined') {
                // todo: something happened with a version of b-toast or vue-portal, if I use b-toast in the template it renders twice
                // so I had to switch to this $bvToast.toast method and comment out the <b-toast> component in the Body
                // the only thing different would be these simple icons
                const icon = this.getters.alertsIconsVariantsMap[alert.variant] || '⚠️'

                if (window?.$root?.$bvToast) {
                    window.$root.$bvToast.toast(alert.message, {
                        key: alert.key,
                        id: alert.key,
                        title: icon + ' ' + _.startCase(alert.title || this.getters.alertsTitlesVariantsMap[alert.variant] || alert.variant || 'Alert'),
                        autoHideDelay: alert.timeout,
                        noCloseButton: !alert.dismissible,
                        variant: alert.variant || 'danger',
                        toaster: `b-toaster-${alert.placement || (this.getters.isSize('lte-md') ? 'bottom-full' : 'bottom-right')}`,
                        toastClass: 'd-print-none border'
                    })
                } else {
                    console.error(alert.message)
                }
            }

            state.alerts.unshift(alert)
            const q = {}
            q[variant] = message
            if (timeout) {
                q.timeout = timeout
                setTimeout(() => {
                    // todo: something happened with a version of b-toast or vue-portal ... as above
                    // Hide the toast
                    // if (window?.$root?.$bvToast) {
                    //      window.$root.$bvToast.hide(alert.key)
                    // }

                    this.commit('clearAlert', { key })
                }, timeout)
            }
            return
        }
        this.commit('clearAlert', { key })
    },
    clearAlert (state, e) {
        const key = e?.key || e
        const i = _.findIndex(state.alerts, a => a.key === key)
        if (i >= 0) {
            state.alerts.splice(i, 1)
        }
    },
    clearAlerts (state) {
        state.alerts = []
    },
    survey (state, survey = {}) {
        state.survey = survey
        if (survey?.id) {
            Vue.set(state.surveys, survey?.id, survey)
        }
    },
    field (state, { field, value }) {
        if (state.survey) {
            Vue.set(state.survey, field, value)
        }
        if (!state.survey?.id) {
            const preSurvey = jsonSafeParse(localStorage.getItem('pre-intake-survey') || '{}')
            Vue.set(preSurvey, field, value)
            localStorage.setItem('pre-intake-survey', jsonSafeStringify(preSurvey))
        }
    },
    year (state, year) {
        state.year = year
    },
    years (state, years = {}) {
        state.years = years
    },
    lastDonutHoleYear (state, lastDonutHoleYear) {
        state.lastDonutHoleYear = lastDonutHoleYear
    },
    soa60days (state, soa60days) {
        state.soa60days = soa60days
    },
    identityVerified (state, verified) {
        state.identityVerified = !!verified
    },
    leavingForVerify (state, leaving) {
        state.leavingForVerify = !!leaving
    },
    surveyAwaitingCompletion (state, awaiting) {
        state.surveyAwaitingCompletion = !!awaiting
    },
    quotes (state, quotes) {
        state.quotes = quotes
    },
    unscrapedQuotes (state, quotes) {
        state.unscrapedQuotes = quotes || []
    },
    quotesInSight (state, quotesInSight) {
        state.quotesInSight = quotesInSight || []
    },
    aepCurrentResolved (state, value) {
        state.aepCurrentResolved = value || null
    },
    quotesCached (state, fromCache) {
        state.quotesCached = !!fromCache
    },
    quoteProgress (state, progress) {
        state.quoteProgress = progress || null
    },
    quotesLight (state, light) {
        state.quotesLight = !!light
    },
    planFiles (state, planFiles) {
        state.planFiles = planFiles || []
    },
    addPlanFile (state, file) {
        const files = state.planFiles.slice(0)
        files.push(file)
        // @vue3-fix
        Vue.set(state, 'planFiles', files)
    },
    editPlanFile (state, file) {
        const files = state.planFiles.slice(0)
        const index = _.findIndex(files, f => f.file_id === file.file_id)
        if (index > -1) {
            files[index] = file
        }
        // @vue3-fix
        Vue.set(state, 'planFiles', files)
    },
    setColor (state, payload) {
        Vue.set(state, payload.isFontColor ? 'fontColor' : 'highlightColor', payload.value)
    },
    setActiveScriptTab (state, tabIndex) {
        Vue.set(state, 'activeScriptTab', tabIndex)
    },
    setActiveSurveyTab (state, tabIndex) {
        Vue.set(state, 'activeSurveyTab', tabIndex)
    },
    removePlanFile (state, file) {
        const files = state.planFiles.slice(0)
        _.remove(files, f => f.file_id === file.file_id)
        // @vue3-fix
        Vue.set(state, 'planFiles', files)
    },
    addGroupPlanFile (state, { plan, file }) {
        const ugp = this.getters.userGroupPlansMap[plan.id]
        if (ugp) {
            const files = (ugp.files || []).slice(0)
            files.push(file)
            this.commit('updateUserGroupPlan', { ...ugp, files })
        }
        const gp = this.getters.groupPlansMap[plan.id]
        if (gp) {
            const files = (gp.files || []).slice(0)
            files.push(file)
            this.commit('updateGroupPlan', { ...gp, files })
        }
    },
    removeGroupPlanFile (state, { plan, file }) {
        const ugp = this.getters.userGroupPlansMap[plan.id]
        if (ugp) {
            const files = (ugp.files || []).slice(0)
            _.remove(files, f => f.file_id === file.file_id)
            this.commit('updateUserGroupPlan', { ...ugp, files })
        }
        const gp = this.getters.groupPlansMap[plan.id]
        if (gp) {
            const files = (gp.files || []).slice(0)
            _.remove(files, f => f.file_id === file.file_id)
            this.commit('updateGroupPlan', { ...gp, files })
        }
    },
    planExtras (state, planExtras) {
        state.planExtras = planExtras
    },
    planNotes (state, planNotes) {
        state.planNotes = planNotes
    },
    addPlanNote (state, note) {
        state.planNotes = [note, ...state.planNotes]
    },
    updatePlanNote (state, note) {
        const idx = state.planNotes.findIndex(n => n.id === note.id)
        if (idx > -1) {
            Vue.set(state.planNotes, idx, { ...state.planNotes[idx], ...note })
        }
    },
    removePlanNote (state, noteId) {
        state.planNotes = state.planNotes.filter(n => n.id !== noteId)
    },
    planExtra (state, payload) {
        const extras = state.planExtras.slice(0)
        const index = _.findIndex(extras, pe => pe.plan_id === payload.plan_id)
        if (index > -1) {
            extras[index] = { ...(extras[index] || {}), ...payload }
        } else {
            extras.push(payload)
        }
        Vue.set(state, 'planExtras', extras)
    },
    summaryScriptsCards (state, cards) {
        state.summaryScriptsCards = cards
    },
    summaryFiles (state, files) {
        Vue.set(state, 'summaryFiles', files)
    },
    addSummaryFile (state, file) {
        const files = state.summaryFiles.slice(0)
        files.push(file)
        // @vue3-fix
        Vue.set(state, 'summaryFiles', files)
    },
    removeSummaryFile (state, file) {
        const files = state.summaryFiles.slice(0)
        _.remove(files, f => f.file_id === file.file_id)
        // @vue3-fix
        Vue.set(state, 'summaryFiles', files)
    },
    sources (state, sources) {
        state.sources = sources
    },
    summaries (state, summaries = []) {
        state.summaries = summaries
    },
    activityChoices (state, activityChoices = []) {
        state.activityChoices = activityChoices
    },
    activities (state, activities = []) {
        state.activities = activities
    },
    commissions (state, commissions = null) {
        state.commissions = commissions
    },
    magicUrl (state, magicUrl) {
        state.magicUrl = magicUrl
    },
    summary (state, summary) {
        state.summary = summary
    },
    summaryData (state, data) {
        Vue.set(state.summary, 'data', data)
    },
    summaryUpdated (state, updated) {
        Vue.set(state.summary, 'updated', updated)
    },
    summaryDataCard (state, { card, ...data }, fromDb = false) {
        Vue.set(state.summary.data.cards, card, { ...data, fromDb })
    },
    // The same list is not a change (2026-10-21 freeze): the AEP engine reads the
    // card list (planBenefitsDocLinked) and Summary.vue's applyAutoCards writes it
    // back from the engine, so a fresh array on every write never settled.
    summaryCards (state, cards = []) {
        const next = _.uniq(cards)
        if (_.isEqual(next, state.summary.data.config.cards)) { return }
        Vue.set(state.summary.data.config, 'cards', next)
    },
    summaryMode (state, mode) {
        Vue.set(state.summary.data.config, 'mode', mode)
    },
    summaryAep (state, aep) {
        Vue.set(state.summary.data.config, 'aep', aep)
    },
    summarySurveyUpdated (state, updated) {
        Vue.set(state.summary.data, 'survey_updated', updated)
    },
    summaryManual (state, manual) {
        Vue.set(state.summary.data, 'manual', manual)
    },
    // #721 AEP builder (spec §2, §3.2): the recommendation is merged over its
    // defaults (nested `current` too) and the legacy mode string is rewritten
    // on every change so summaryMode readers keep working.
    summaryRecommendation (state, patch = {}) {
        const config = state.summary.data.config
        const prev = config.recommendation || {}
        const next = {
            ...aep.DEFAULT_RECOMMENDATION,
            ...prev,
            ...patch,
            current: { ...aep.DEFAULT_RECOMMENDATION.current, ...(prev.current || {}), ...(patch.current || {}) },
            // round-2 spec §13: replaced whole by a patch, always a fresh array
            alternatives: (Array.isArray(patch.alternatives) ? patch.alternatives : (prev.alternatives || [])).map(a => ({ ...a })),
        }
        Vue.set(config, 'recommendation', next)
        const surveyCoverage = state.survey?.current_medicare_coverage
        const coverage = aep.COVERAGES.includes(next.coverage) ? next.coverage : (aep.COVERAGES.includes(surveyCoverage) ? surveyCoverage : null)
        Vue.set(config, 'mode', aep.scenario(coverage, next).mode)
    },
    summaryCardManual (state, { key, value }) {
        const config = state.summary.data.config
        if (!config.cards_manual) { Vue.set(config, 'cards_manual', {}) }
        Vue.set(config.cards_manual, key, !!value)
    },
    summaryCardManualReset (state, key) {
        const manual = state.summary.data.config.cards_manual
        if (manual && key in manual) { Vue.delete(manual, key) }
    },
    summaryCardsManualPrune (state, keepComboKeys) {
        const keep = [].concat(keepComboKeys || [])
        const manual = state.summary.data.config.cards_manual || {}
        Object.keys(manual).filter(k => /^combo_/.test(k) && !keep.includes(k)).forEach(k => Vue.delete(manual, k))
    },
    addSummaryPlan (state, plan) {
        Vue.set(state.summary.data.plans, plan.id, { id: plan.id, recommendation: plan.recommendation })
    },
    removeSummaryPlan (state, plan) {
        const re = new RegExp(`${plan.id}`)
        const idx = _.findIndex(state.summary.data.config.cards, card => re.test(card))
        if (idx > -1) {
            Vue.delete(state.summary.data.config.cards, idx)
        }
        Vue.delete(state.summary.data.plans, plan.id)
    },
    userGroupPlans (state, userGroupPlans) {
        state.userGroupPlans = userGroupPlans
    },
    updateUserGroupPlan (state, userGroupPlan) {
        return this.commit('addUserGroupPlan', userGroupPlan)
    },
    addUserGroupPlan (state, userGroupPlan) {
        if (!userGroupPlan) {
            return
        }
        const userGroupPlans = state.userGroupPlans
        const index = _.findIndex(userGroupPlans, ugp => ugp.id === userGroupPlan.id)
        if (index === -1) {
            userGroupPlans.push(this.getters.parsePlan(userGroupPlan))
            state.userGroupPlans = userGroupPlans
        } else {
            Vue.set(state.userGroupPlans, index, this.getters.parsePlan(userGroupPlan))
        }
    },
    addSummaryUserGroupPlan (state, userGroupPlan) {
        if (!userGroupPlan) {
            return
        }
        this.commit('addUserGroupPlan', userGroupPlan)

        if (!state.summary.data.user_group_plans) {
            Vue.set(state.summary.data, 'user_group_plans', {})
        }
        Vue.set(state.summary.data.user_group_plans, userGroupPlan.id, { id: userGroupPlan.id })
    },
    removeSummaryUserGroupPlan (state, userGroupPlan) {
        Vue.delete(state.summary.data?.user_group_plans || {}, userGroupPlan?.id)
    },
    groupPlans (state, groupPlans) {
        state.groupPlans = groupPlans
    },
    updateGroupPlan (state, groupPlan) {
        return this.commit('addGroupPlan', groupPlan)
    },
    addGroupPlan (state, groupPlan) {
        if (!groupPlan) {
            return
        }
        const groupPlans = state.groupPlans
        const index = _.findIndex(groupPlans, gp => gp.id === groupPlan.id)
        if (index === -1) {
            groupPlans.push(this.getters.parsePlan(groupPlan))
            state.groupPlans = groupPlans
        } else {
            Vue.set(state.groupPlans, index, this.getters.parsePlan(groupPlan))
        }
    },
    support (state, support) {
        state.support = support
        localStorage.setItem('support', support)
    },
    sidebar (state, sidebar) {
        state.sidebar = sidebar
        localStorage.setItem('sidebar', sidebar)
    },
    addComparing (state, plan) {
        Vue.set(state.comparing, plan.id, plan)
    },
    removeComparing (state, plan) {
        Vue.delete(state.comparing, plan.id, plan)
    },
    clearComparing (state) {
        state.comparing = {}
    },
    opportunities (state, opportunities = []) {
        state.opportunities = opportunities
    },
    activeProducts (state, activeProducts = []) {
        state.activeProducts = activeProducts
    },
    activeBreakpoint (state, activeBreakpoint) {
        state.activeBreakpoint = activeBreakpoint
    },
    autosave (state, status) {
        clearTimeout(state.autosaveTimeout)
        state.autosave = status
        state.autosaveTimeout = setTimeout(() => {
            this.commit('autosave', null)
        }, 2300)
    },
    usStates (state, usStates = []) {
        state.usStates = usStates
    },
    county (state, county) {
        state.county = county
    }
}

export const actions = {
    nuxtServerInit ({ commit, dispatch }, { route, req, ssrContext }) {
        const STATE = req?.body?.STATE || ssrContext?.req?.body?.STATE
        commit('state', STATE)
        commit('route', route)
        // return dispatch('loadUsStates')
    },
    async nuxtClientInit ({ commit, dispatch }, { req }) {
        if (req?.body?.STATE) {
            commit('state', req?.body?.STATE)
        } else if (window?.STATE) {
            commit('state', window?.STATE)
        } else {
            await dispatch('loadState')
        }
        if (typeof window !== 'undefined') {
            commit('support', localStorage.getItem('support') === 'true')
        }
        return dispatch('loadUsStates')
    },
    async loadState ({ commit }) {
        const STATE = await this.$try('getState')
        if (!STATE?.error) {
            commit('state', STATE)
        }
    },
    async register ({ commit }, payload) {
        const redirectTo = payload.redirectTo
        delete payload.redirectTo
        const data = await this.$try('register', payload)
        if (!data?.error) {
            commit('user', data.user)
            if (redirectTo !== false) {
                this.$router.push(redirectTo || '/')
            }
        }
        return data
    },
    async login ({ commit }, payload) {
        const redirectTo = payload.redirectTo
        delete payload.redirectTo
        const data = await this.$try('login', payload)
        if (!data?.error) {
            commit('user', data.user)
            commit('group', data.group)
            commit('soa60days', data.soa60days)
            if (redirectTo !== false) {
                this.$router.push(redirectTo || '/')
            }
        }
        return data
    },
    async logout ({ commit }, redirectTo) {
        await this.$try('logout')
        window.$root.$emit('logout')
        commit('user', null)
        commit('survey', null)
        if (redirectTo !== false) {
            this.$router.push(redirectTo || '/')
        }
    },
    async sendVerifyEmail ({ commit }) {
        const data = await this.$try('emailVerifyStart')
        if (!data?.error) {
            commit('pushAlert', { variant: 'info', message: 'Email sent!' })
        }
    },
    busy ({ commit }) {
        commit('busy', true)
    },
    idle ({ commit }) {
        commit('busy', false)
    },
    alert ({ commit }, e) {
        commit('pushAlert', e)
    },
    info ({ commit }, message) {
        commit('pushAlert', { ...(_.isObject(message) ? message : { message }), variant: 'info' })
    },
    warning ({ commit }, message) {
        commit('pushAlert', { ...(_.isObject(message) ? message : { message }), variant: 'warning' })
    },
    error ({ commit }, message) {
        commit('pushAlert', { ...(_.isObject(message) ? message : { message }), variant: 'danger' })
    },
    success ({ commit }, message) {
        commit('pushAlert', { ...(_.isObject(message) ? message : { message }), variant: 'success' })
    },
    unalert ({ commit }) {
        commit('clearAlerts')
    },
    // Oracle's year list without a survey (/search needs it to pick a default
    // year). getSurvey loads the same list, so once either has run this is a
    // no-op. A failure leaves the list empty, and defaultYear then stays on
    // the calendar year.
    async loadYears ({ commit, getters }) {
        if (getters.oracleYears.length) {
            return
        }
        const resp = await this.$try('getSurveyYears', { spinner: false, alert: false })
        if (resp && !resp.error) {
            commit('years', resp.years)
            commit('lastDonutHoleYear', resp.last_donut_hole_year)
        }
    },
    async loadAllClientUserGroupPlans ({ commit, getters }) {
        const results = await this.$try('getAllUserGroupPlans', { group: getters.groupId, user: getters.client?.id }, { spinner: false })
        commit('userGroupPlans', results.map(gp => getters.parsePlan(gp)))
    },
    async getSurvey ({ commit, getters }, { id, spinner = false, aep = false, alert = null } = {}) {
        aep = aep || (!getters.canAgent && getters.isAEPScheduling && (/aep/i.test(getters.route.path + '__' + jsonSafeStringify(getters.route.query || {}))))
        /* eslint-disable camelcase */
        const {
            error,
            survey,
            years,
            magic_url,
            soa60days,
            summaries,
            activities,
            commissions,
            last_donut_hole_year,
        } = await this.$try(getters.user ? 'getSurvey' : 'getAnonSurvey', { id, aep }, { spinner, alert })

        if (!error) {
            // todo: rm hack
            ['providers', 'drugs', 'pharmacies', 'hospitals'].forEach((prop) => {
                if (!_.isArray(survey[prop])) {
                    survey[prop] = []
                }
            })
            survey.drugs = (survey.drugs || []).map(drg => getters.parseDrug(drg))
            commit('years', years)
            commit('lastDonutHoleYear', last_donut_hole_year)
            commit('soa60days', soa60days)
            commit('summaries', summaries)
            commit('activities', activities)
            commit('commissions', commissions)
            commit('survey', survey)
            commit('magicUrl', magic_url)
            return survey
        }
    },
    async includeDrug ({ state, getters, dispatch }, drug) {
        const drugs = _.cloneDeep(state.survey?.drugs || [])
        const idx = _.findIndex(drugs, d => getters.getDrugUniquishKey(d) === getters.getDrugUniquishKey(drug))
        if (idx > -1) {
            const drg = drugs[idx]
            drg.excluded = false
            await dispatch('updateSurvey', { id: getters.uid, data: { drugs }, nextTick: true })
            return true
        }
    },
    async excludeDrug ({ state, getters, dispatch }, drug) {
        const drugs = _.cloneDeep(state.survey?.drugs || [])
        const idx = _.findIndex(drugs, d => getters.getDrugUniquishKey(d) === getters.getDrugUniquishKey(drug))
        if (idx > -1) {
            const drg = drugs[idx]
            drg.excluded = true
            await dispatch('updateSurvey', { id: getters.uid, data: { drugs }, nextTick: true })
            return true
        }
    },
    async updateSurvey ({ commit }, { id, data, nextTick }) {
        const commits = () => {
            for (const field in data) {
                commit('field', { field, value: data[field] })
            }
        }
        // why nextTick? because removing items from a list won't emit events from the already removed items
        // so we call the api, then we remove in a nextTick aka setTimeout in a store
        if (!nextTick) {
            commits()
        }
        const response = await this.$api.updateSurvey({ id, data })
        if (nextTick) {
            setTimeout(commits, 0)
        }
        return response
    },
    completeSurvey (options = {}) {
        return this.$api.completeSurvey(options)
    },
    // #721 round 2 (spec §5.1): never blocks the summary; a failure leaves the
    // name matcher in charge.
    async loadAepCurrentPlans ({ commit }, { id, year } = {}) {
        // review M4: never check the previous client's ids against this one's quotes
        commit('aepCurrentResolved', null)
        try {
            const data = await this.$api.getAepCurrentPlans({ id, year })
            commit('aepCurrentResolved', data && data.lines ? data : null)
        } catch (e) {
            commit('aepCurrentResolved', null)
        }
    },
    async getQuotes ({ commit, getters, dispatch }, { id, year, fresh, detailed, supported = false, commissionable_only = false, light = false, spinner, progress = false } = {}) {
        if (!id) {
            id = getters.uid
        }
        if (!year) {
            year = getters.year
        }
        fresh = fresh || false
        detailed = detailed || false
        spinner = spinner || false

        // With `progress`, Oracle reports how far the quote has got under a fresh
        // id, polled while the request runs, for the spinner to show (flow#735).
        // Straight through $api: $try would set the store idle after every poll.
        const progressId = progress ? newProgressId() : undefined
        const stopPolling = progress
            ? pollProgress({
                fetch: () => this.$api.getQuoteProgress(progressId),
                onProgress: snapshot => commit('quoteProgress', snapshot),
            })
            : null
        let data
        try {
            data = await this.$try('getQuotes', { id, year, fresh, detailed, supported, commissionable_only, light, progress_id: progressId }, { spinner })
        } finally {
            if (stopPolling) {
                stopPolling()
                commit('quoteProgress', null)
            }
        }
        // eslint-disable-next-line camelcase
        const { quotes, sources, error, plan_files, plan_extras, plan_notes, from_cache, unscraped_quotes } = data
        if (!error) {
            commit('planExtras', plan_extras)
            commit('planNotes', plan_notes || [])
            commit('planFiles', plan_files)
            const plans = quotes.map(getters.parseQuote)
            commit('quotes', plans)
            // Agent-only: unscraped plans are an IT-facing catalog-drift signal and must
            // never reach a client, so clients don't even receive them into state.
            commit('unscrapedQuotes', getters.canAgent ? (unscraped_quotes || []) : [])
            commit('quotesCached', from_cache)
            commit('quotesLight', data.light)
            commit('sources', sources)
            await dispatch('loadCountyFromZipCode')
            return { quotes: plans, plans, sources, planFiles: plan_files, planExtras: plan_extras, fromCache: from_cache, light: data.light }
        } else {
            return { error }
        }
    },
    async createSummary ({ commit }, { user, year }) {
        const summary = await this.$api.createSummary({
            year,
            user,
        })
        return summary
    },
    async getSummary ({ state, getters, dispatch, commit }, { id, uid, year } = {}) {
        if (!year) {
            year = getters.year
        }
        if (!uid) {
            uid = getters.uid
        }
        if (!id) {
            id = (await dispatch('createSummary', { user: getters.uid, year: getters.year }))?.id
        }
        const summary = await this.$try('getSummary', id, { spinner: false })
        if (!summary || !_.isObject(summary) || summary.error) {
            console.error('[store.getSummary] Error getting summary', summary)
            return null
        }
        summary.data.plans = _.mapValues(summary.data.plans, (plan) => {
            return getters.parsePlan(plan)
        })
        commit('summary', summary)
        commit('summaryData', getters.summaryMerge(summary?.data))
        return state.summary
    },
    async getSummaryFiles ({ commit }, { id } = {}) {
        const summaryFiles = await this.$try('getSummaryFiles', id, { spinner: false })
        commit('summaryFiles', summaryFiles)
    },
    async updateSummary ({ state, commit, getters }, { id, data }) {
        data = getters.summaryMerge(data)
        const resp = await this.$try('updateSummary', { id: id || state.summary.id, data })
        if (!resp?.error) {
            commit('summaryData', data)
            // the send dialog and the staff banner say "edited since" from it
            if (resp?.updated) { commit('summaryUpdated', resp.updated) }
        }
    },
    async resetSummary ({ state, commit, getters }, { id, data }) {
        const results = await this.$try('resetSummary', { id: id || state.summary.id, data })
        if (!results.error) {
            commit('summaryData', results.data)
        }
    },
    addSummaryUserGroupPlan ({ state, commit }, plan) {
        commit('addSummaryUserGroupPlan', plan)
        return this.$api.updateSummary({ id: state.summary.id, data: state.summary.data })
    },
    removeSummaryUserGroupPlan ({ state, commit }, plan) {
        commit('removeSummaryUserGroupPlan', plan)
        return this.$api.updateSummary({ id: state.summary.id, data: state.summary.data })
    },
    addSummaryPlan ({ state, commit }, plan) {
        commit('addSummaryPlan', plan)
        return this.$api.updateSummary({ id: state.summary.id, data: state.summary.data })
    },
    removeSummaryPlan ({ state, commit }, plan) {
        commit('removeSummaryPlan', plan)
        return this.$api.updateSummary({ id: state.summary.id, data: state.summary.data })
    },
    async getOpportunities ({ commit }, uid) {
        const resp = await this.$api.getOpportunities(uid)
        if (resp?.opportunities) {
            commit('opportunities', resp?.opportunities)
        }
    },
    loadSummaryUserGroupPlans ({ state, commit }) {
        return Promise.all(_.map(state.summary.data?.group_plans, async (gp) => {
            const userGroupPlan = await this.$api.getUserGroupPlan(gp.id)
            commit('addUserGroupPlan', userGroupPlan)
        }))
    },
    async loadSummaryScriptsCards ({ state, commit }) {
        const { items } = await this.$api.getItems({ type: 'summary-scripts' })
        commit('summaryScriptsCards', items)
    },
    async getActiveProducts ({ commit }, uid) {
        const { products } = await this.$api.getActiveProducts(uid)
        commit('activeProducts', products)
    },
    onSurveyDone ({ getters }, options = {}) {
        let res
        if (getters.canAgent) {
            res = this.$try('agentCompleteSurvey', getters.uid, options)
        } else {
            res = this.$try('completeSurvey', options)
        }
        if (!res?.error) {
            this.$success('Survey Completed!')
            if (getters.canAgent) {
                return this.$router.push(`/survey/${getters.uid}`)
            }
            if (options.aep) {
                this.$router.push({ path: '/thank-you', query: { ...(getters.query || {}), aep: options.aep ? 1 : null, prec: options.prec ? 1 : null } })
            } else {
                this.$router.push({ path: '/survey', query: getters.query || {} })
            }
        }
    },
    updateActiveBreakpoint ({ commit }) {
        commit('activeBreakpoint', getActiveBreakpoint())
    },
    async loadUsStates ({ commit }) {
        const usStates = await this.$try('getUsStates')
        commit('usStates', usStates)
    },
    async loadCountyFromZipCode ({ commit, state }) {
        if (state.survey?.zip_code && state.survey?.zip_code !== state.county?.zip_code) {
            const resp = await this.$api.getCountiesForZip(state.survey?.zip_code)
            if (resp) {
                let county = _.find(resp?.counties, county => county.fips === state.survey?.fips)
                if (!county) {
                    county = resp?.counties[0]
                }
                commit('county', { zip_code: state.survey?.zip_code, ...county })
            }
        }
    },
    async ignorePlanOrignalFile ({ commit }, { plan }) {
        await this.$try('updatePlanExtras', { plan_id: plan.id, ignore_original_file: true })
        commit('planExtra', { plan_id: plan.id, ignore_original_file: true })
    },
    async unignorePlanOrignalFile ({ commit }, { plan }) {
        await this.$try('updatePlanExtras', { plan_id: plan.id, ignore_original_file: false })
        commit('planExtra', { plan_id: plan.id, ignore_original_file: false })
    },
    async updatePlanDrugDeductibleApplicableTiers ({ commit }, { plan, tiers }) {
        await this.$try('updatePlanExtras', { plan_id: plan.id, drug_deductible_applicable_tiers: tiers })
        commit('planExtra', { plan_id: plan.id, drug_deductible_applicable_tiers: tiers })
    },
    async createPlanNote ({ commit }, data) {
        const note = await this.$api.createPlanNote(data)
        if (note && !note.error) {
            commit('addPlanNote', note)
        }
        return note
    },
    async savePlanNote ({ commit }, { id, ...data }) {
        const note = await this.$api.updatePlanNote(id, data)
        if (note && !note.error) {
            commit('updatePlanNote', note)
        }
        return note
    },
    async deletePlanNote ({ commit }, noteId) {
        const res = await this.$api.deletePlanNote(noteId)
        if (res?.success) {
            commit('removePlanNote', noteId)
        }
        return res
    },
    async getActivityChoices ({ commit }) {
        const activityChoices = await this.$try('getActivityChoices')
        if (!activityChoices?.error) {
            commit('activityChoices', activityChoices || [])
        }
    }
}

export const getters = {
    // Set from window.STATE (flow/accounts/state.py) by the `state` mutation. True on
    // a demo instance, where several CRM-backed features are deliberately switched
    // off — pages read this to explain themselves rather than look broken.
    demo (state) { return !!state.demo },
    lastDonutHoleYear (state) { return state.lastDonutHoleYear },
    isDonutHoleYear (state, getters) { return getters.year <= getters.lastDonutHoleYear },
    soa60days (state) { return state.soa60days },
    identityVerified (state) { return !!state.identityVerified },
    surveyAwaitingCompletion (state) { return !!state.surveyAwaitingCompletion },
    // Both come straight off /state/ (flow/accounts/state.py), so the redirect
    // costs no extra request. `identityVerified` is the local echo set once a
    // challenge is cleared, so the page doesn't re-ask before /state/ refreshes.
    identityPending (state) { return !!state.identity_pending && !state.identityVerified },
    identityChallenge (state) { return state.identity_challenge || null },
    highlightColor (state) { return state.highlightColor },
    fontColor (state) { return state.fontColor },
    activeScriptTab (state) { return state.activeScriptTab },
    activeSurveyTab (state) { return state.activeSurveyTab },
    assetsBase (state) { return state.assetsBase },
    user (state) { return state.user },
    originalState (state) { return state.originalState },
    agent (state, getters) { return getters.canAgent ? getters.user : getters.client?.agent || getters.user?.agent || state.agent },
    group (state, getters) { return getters.asGroup ? state.group || state.originalState?.group : getters.canAgent ? state.originalState?.group : state.group },
    asGroup (state, getters) { return getters.canAgent && state.group?.id !== state.originalState?.group?.id && getters.slug },
    groupName (state, getters) { return getters.client?.group?.name || getters?.group?.name },
    groupSlug (state, getters) { return getters.client?.group?.slug || getters?.group?.slug },
    groupPaying (state, getters) { return getters.client?.group?.paying || getters?.group?.paying },
    groupIchra (state, getters) { return getters.client?.group?.ichra || getters?.group?.ichra },
    groupIchraTpa (state, getters) { return getters.client?.group?.ichra_tpa || getters?.group?.ichra_tpa },
    // Group.config['hsa_note']: a per-employer standing fact about that
    // employer's health plan, shown to staff beside the HSA question so they
    // don't take the client's answer at face value (Fairfield County Bank's
    // plan carries an employer-funded HSA, so employees answer 'no' when the
    // true answer is yes). Any group can carry one; no group without the key
    // shows anything.
    groupHsaNote (state, getters) { return getters.client?.group?.config?.hsa_note || getters?.group?.config?.hsa_note },
    groupFinancialAdvisor (state, getters) { return getters.client?.group?.financial_advisor || getters?.group?.financial_advisor },
    groupDPC (state, getters) { return getters.client?.group?.dpc || getters?.group?.dpc },
    groupId (state, getters) { return getters.client?.group?.id || getters?.group?.id },
    inGroup (state, getters) { return !!getters.groupId },
    client (state) { return state.survey || (state.user?.role === 'client' ? state.user : null) },
    surveyLoggedIn (state, getters) { return !!state.survey?.id },
    surveyLoggedOut (state, getters) { return !getters.surveyLoggedIn },
    magicUrl (state) { return state.magicUrl },
    aepMagicUrl (state, getters) { return getters.magicUrl ? getters.magicUrl.replace(/\/survey\/.*/, '/survey/aep?ft=1') : null },
    soaMagicUrl (state, getters) { return getters.magicUrl ? getters.magicUrl.replace(/\/survey\/.*/, '/survey/soa') : null },
    // Straight to /checkup/welcome for everyone. This used to branch on service
    // tier — fullserve to /survey/welcome, selfserve to /survey/step-by-step —
    // but both of those are now redirectors to /checkup/welcome, so the branch
    // only bought the client an extra hop on a link we email them.
    preconsultMagicUrl (state, getters) { return getters.magicUrl ? getters.magicUrl.replace(/\/survey\/.*/, '/checkup/welcome') : null },
    preferredFirstName (state, getters) { return _.startCase(state.survey?.preferred_first_name || '') || _.startCase(state.survey?.preferred_name || '') || getters.firstName },
    firstName (state) { return _.startCase(state.survey?.first_name || '') || null },
    lastName (state) { return _.startCase(state.survey?.last_name || '') || null },
    clients (state) { return (state.clients || []) },
    crmClient (state) { return state.crmClient },
    matchingClients (state) { return (state.matchingClients || []) },
    alerts (state) { return state.alerts },
    isBusy (state) { return state.busy },
    isServer () { return process.server },
    activeBreakpoint (state) { return state.activeBreakpoint },
    activeBreakpointClassName (state, getters) { return getters.activeBreakpoint?.className || '' },
    activeBreakpointClassMap (state, getters) { return getters.activeBreakpoint?.classMap || {} },
    isSize (state, getters) { return size => !!getters.activeBreakpointClassMap[size] },
    isClient (state, getters) { return state?.user?.role === 'client' },
    isHr (state, getters) { return state?.user?.role === 'hr' },
    isBroker (state, getters) { return state?.user?.role === 'broker' },
    isAgent (state, getters) { return state?.user?.role === 'agent' },
    isAdmin (state) { return state?.user?.role === 'admin' },
    isSu (state) { return state?.user?.role === 'su' },
    canClient (state, getters) { return getters.isClient || getters.canHr },
    canHr (state, getters) { return getters.isHr || getters.canBroker },
    canBroker (state, getters) { return getters.isBroker || getters.canAgent },
    canAgent (state, getters) { return getters.isAgent || getters.canAdmin },
    canAdmin (state, getters) { return getters.isAdmin || getters.canSu },
    canSu (state, getters) { return getters.isSu },
    brokerGroups (state) { return state.brokerGroups || [] },
    isAgentMode (state, getters) { return getters.routeUid && getters.canAgent },
    support (state) { return state.support },
    survey (state, getters) { return getters.uid ? state.surveys[getters.uid] : state.survey },
    zipCode (state, getters) { return getters.survey?.zip_code },
    county (state, getters) { return state.county || _.pick(getters.client || {}, ['zip_code', 'fips', 'state', 'county']) },
    surveys (state) { return state.surveys },
    planExtras (state, getters) { return state.planExtras || [] },
    planExtrasMap (state, getters) { return _.keyBy(state.planExtras || [], 'plan_id') },
    planNotes (state) { return state.planNotes || [] },
    // Oracle's endpoints/plan_note_search.py `note_applies` ports this rule for the agent Plan Notes page; change both together.
    planNotesForPlan (state, getters) {
        return (plan) => {
            const userState = getters.survey?.state
            return (state.planNotes || []).filter((n) => {
                // oracle ships active notes only; one switched off in the note window leaves the card too
                if (n.active === false) return false
                if (n.plan_id) return n.plan_id === plan.id
                // a note on the bare company name also covers its rate classes ("X" -> "X (Standard II)");
                // matched on the catalog carrier, not the display rewrite (planCarriersReplace)
                const carrier = plan.original_carrier || plan.carrier
                const carrierMatch = !n.carrier || n.carrier === carrier || n.carrier === carrierCompanyName(carrier)
                // states_mode: all = match all, include = user_state must be in list, exclude = user_state must NOT be in list
                let stateMatch = true
                const mode = n.states_mode
                const states = n.states || []
                if (mode === 'include' && states.length) {
                    stateMatch = userState && states.includes(userState)
                } else if (mode === 'exclude' && states.length) {
                    stateMatch = !userState || !states.includes(userState)
                }
                const typeMatch = !n.plan_type || n.plan_type === plan.plan_type
                const subtypeMatch = !n.plan_subtype || !n.plan_subtype.length || n.plan_subtype.includes(plan.plan_subtype)
                return carrierMatch && stateMatch && typeMatch && subtypeMatch
            })
        }
    },
    hasPlanPreferredName (state, getters) { return plan => getters.planExtrasMap[plan.id]?.preferred_name },
    isPlanOriginalFileIgnored (state, getters) { return plan => !!getters.planExtrasMap[plan.id]?.ignore_original_file },
    // { tiers, all, source } a plan's drug deductible applies to, or null to
    // guess from the client's drugs (#731, util/deductible-tiers.js). A parsed
    // plan keeps its quote's CMS values in cms_drug_deductible, because
    // parsePlan overwrites the quote fields with the resolved tiers.
    planDrugDeductibleTiers (state, getters) {
        return (plan) => {
            const cms = 'cms_drug_deductible' in plan ? plan.cms_drug_deductible : cmsDeductibleTiers(plan)
            return resolveDeductibleTiers(getters.planExtrasMap[plan.id]?.drug_deductible_applicable_tiers, cms)
        }
    },
    isGroupForceReferral (state, getters) {
        return () => !!state?.group?.config?.force_referral
    },
    getCurrentContext (state, getters) {
        return () => {
            const pageContext = {
                client: getters.client,
                group: getters.group,
                // plans: getters.plans,
                summary: getters.summary,
                currentPageContent: window.$root.$el.querySelector('.survey-view__content')?.innerText,
            }
            return { pageContext, planIds: [] }
        }
    },
    planFiles (state, getters) { return state.planFiles || [] },
    planFilesMap (state, getters) { return _.keyBy(state.planFiles || [], 'file_id') },
    planFilesByPlanId (state, getters) {
        return _.groupBy(state.planFiles, 'plan_id')
    },
    planFilesInState (state, getters) {
        return getters.planFiles.filter((pf) => {
            if (!pf?.metadata?.available_states?.length) {
                return true
            }
            return pf?.metadata?.available_states.some(usState => usState === getters.county?.state)
        })
    },
    planFilesOutState (state, getters) {
        return getters.planFiles.filter((pf) => {
            if (!pf?.metadata?.available_states?.length) {
                return false
            }
            return pf?.metadata?.available_states.every(usState => usState !== getters.county?.state)
        })
    },
    planFilesByPlanIdInState (state, getters) {
        return _.groupBy(getters.planFilesInState, 'plan_id')
    },
    planFilesByPlanIdOutState (state, getters) {
        return _.groupBy(getters.planFilesOutState, 'plan_id')
    },
    summaryScriptsCards (state) { return state.summaryScriptsCards || [] },
    summaryScriptsCardsMap (state, getters) {
        return _.keyBy(getters.summaryScriptsCards, 'key')
    },
    summaryFiles (state, getters) { return state.summaryFiles || [] },
    summaryFilesMap (state, getters) { return _.keyBy(getters.summaryFiles, 'file_id') },
    summarySwitchedOnCardsList (state, getters) {
        return _.uniq((getters.summary?.data?.config?.cards || []).filter(c => !!c))
    },
    summarySwitchedOnCardsMap (state, getters) {
        return _.keyBy(getters.summarySwitchedOnCardsList, card => card)
    },
    summarySwitchedOnCardsCombos (state, getters) {
        return getters.summarySwitchedOnCardsList.filter(c => /combo_/.test(c))
    },
    summaryCombosPlans (state, getters) {
        let plans = [];
        (getters.summarySwitchedOnCardsCombos || []).forEach((combo) => {
            plans = plans.concat(getters.getSummaryComboPlansByCard(combo))
        })
        return _.uniqBy(plans, 'id')
    },
    summaryCombosOrderedAsCards (state, getters) {
        const combos = [];
        (getters.summarySwitchedOnCardsCombos || []).forEach((combo) => {
            const plans = getters.getSummaryComboPlansByCard(combo)
            combos.push(plans)
        })
        return combos
    },
    getSummaryComboPlanIdsByCard (state, getters) {
        return card => card.replace(/combo_/, '').split('_')
    },
    getSummaryComboPlansByCard (state, getters) {
        return (card) => {
            const ids = getters.getSummaryComboPlanIdsByCard(card)
            const plans = ids.map(id => getters.summaryPlansMap[id]).filter(p => p)
            return plans
        }
    },
    summaryPlansAttachments (state, getters) {
        let plansList = getters.summaryCombosPlans
        // Kalena 2026-09-30 (2): the builder's combo holds the resulting plans
        // only; the client also needs the current plans' documents. A plan not
        // offered this year never resolves to a store plan, so it stays out.
        if (getters.summaryAep && getters.aepActive) {
            const current = (getters.aepContext?.replacedAll || []).map(p => getters.summaryPlansMap[p.id]).filter(Boolean)
            plansList = _.uniqBy(plansList.concat(current), 'id')
        }
        let attachments = []
        plansList.forEach((plan) => {
            const planFiles = (getters.planFilesByPlanIdInState[plan.id] || []).slice(0)
            if (plan.summary_pdf && !getters.isPlanOriginalFileIgnored(plan)) {
                planFiles.unshift({
                    full_path: plan.summary_pdf,
                    file_name: `${plan.name} Summary Of Benefits.pdf`
                })
            }
            attachments = attachments.concat(planFiles)
            return planFiles
        })
        return attachments
    },
    // Whether the Attachments card at the end of the summary links one of the
    // plan's own documents (the two sources summaryPlansAttachments lists for
    // it), so the AEP Medicare Advantage paragraph can point there
    // (2026-10-21). Few 2027 plans have one uploaded.
    planBenefitsDocLinked (state, getters) {
        return (plan) => {
            if (!plan || !getters.summaryCardsMap.attachments) { return false }
            const excluded = _.get(state.summary, 'data.cards.attachments.config.value.excluded') || []
            const docs = (getters.planFilesByPlanIdInState[plan.id] || []).slice(0)
            if (plan.summary_pdf && !getters.isPlanOriginalFileIgnored(plan)) {
                docs.push({ full_path: plan.summary_pdf })
            }
            return docs.some(doc => !isAttachmentExcludedBy(doc, excluded))
        }
    },
    summaryAttachments (state, getters) {
        let attachments = getters.summaryPlansAttachments
        attachments = attachments.concat(getters.summaryFiles || [])

        // if (getters.summaryCardsMap.step_ichra && !getters.survey?.collecting_ssi) {
        //     attachments = attachments.concat({
        //         full_path: 'https://doctorschoice.s3.amazonaws.com/common-pdfs/Medicare.EasyPay.OneTime.Payment.Setup.Tutorial.pdf',
        //         file_name: 'ICHRA Medicare EasyPay Tutorial.pdf'
        //     })
        // }
        if (getters.summaryCardsMap.step_part_b_signup) {
            attachments = attachments.concat({
                full_path: 'https://www.cms.gov/Medicare/CMS-Forms/CMS-Forms/Downloads/CMS40B-E.pdf',
                file_name: 'CMS40B-E.pdf'
            })
            attachments = attachments.concat({
                full_path: 'https://www.cms.gov/medicare/CMS-Forms/CMS-Forms/Downloads/CMS-L564E.pdf',
                file_name: 'CMS-L564E.pdf'
            })
        }
        return attachments
    },
    plans (state, getters) { return state.quotes.map(getters.parsePlan) },
    // Display-only medicare.gov plans with no matching DB row (id: null). Run them
    // through the same parsePlan as real quotes, then stamp a collision-free key —
    // parsePlan sets `plan.key = plan.id`, which is null for every unscraped plan and
    // useless for v-for. See docs/superpowers/specs/2026-07-24-unscraped-quotes-design.md
    unscrapedPlans (state, getters) {
        return (state.unscrapedQuotes || []).map((q) => {
            const plan = getters.parsePlan(q)
            plan.key = plan.source_id
                ? `unscraped-${plan.source_id}`
                : `unscraped-${plan.plan_type}-${plan.supp_type || plan.plan_subtype || ''}-${plan.carrier || ''}`
            return plan
        })
    },
    plansMap (state, getters) { return _.keyBy(getters.plans, q => q.id) },
    plansSourcesMap (state, getters) { return _.keyBy(getters.plans, q => q.source_id) },
    sources (state) { return state.sources },
    summary (state) { return state.summary },
    activities (state) { return state.activities },
    commissions (state) { return state.commissions },
    lastCheckupCompleteActivity (state) { return _.find(state.activities, activity => activity.activity === 'complete_survey') },
    quotesInSight (state) { return state.quotesInSight || [] },
    quotesCached (state) { return state.quotesCached || false },
    quoteProgress (state) { return state.quoteProgress || null },
    quotesLight (state) { return state.quotesLight || false },
    asFullserve (state, getters) {
        return getters.client?.as_fullserve
    },
    isPlanCommissionable (state, getters) {
        return (plan) => {
            return !plan.non_commissionable || (_.isBoolean(plan.non_commissionable?.states) ? !plan.non_commissionable?.states : !(plan.non_commissionable?.states || []).includes(getters.client?.state))
        }
    },
    isPlanContractable (state, getters) {
        return (plan) => {
            return !plan.non_contractable || (_.isBoolean(plan.non_contractable?.states) ? !plan.non_contractable?.states : !(plan.non_contractable?.states || []).includes(getters.client?.state))
        }
    },
    isPlanEnrollable (state, getters) {
        return (plan) => {
            return !plan.non_enrollable || (_.isBoolean(plan.non_enrollable?.states) ? !plan.non_enrollable?.states : !(plan.non_enrollable?.states || []).includes(getters.client?.state))
        }
    },
    fromDCI (state, getters) {
        return getters.client?.source_type === 'DOCTOR_CHOICE_INTERNAL'
    },
    fromDPC (state, getters) {
        return getters.groupDPC
    },
    fullserve (state, getters) {
        return getters.client?.as_fullserve || getters.fromDPC || getters.fromDCI || !!getters.groupPaying || !!getters.groupFinancialAdvisor
    },
    fullservePlus (state, getters) {
        return getters.fromDCI || !!getters.groupPaying || !!getters.groupFinancialAdvisor
    },
    selfserve (state, getters) {
        return !getters.fullserve
    },
    // The FL / RI rule governs this client (the map marker). Only the backend's
    // verdict counts: it comes on the survey payload, the agent's and the
    // client's own (c_open_state).
    openStateClient (state, getters) {
        return isOpenStateClient(getters.client)
    },
    // Kalena 2026-10-06: "the non-paying clients we assist" -- served by email
    // (the star) or under the FL / RI rule (the map marker). Not paying,
    // internal, financial-advisor or DPC. The plan card says Enrollable /
    // Non-Enrollable to them in one word (util/plan-labels.js).
    assistedNonPaying (state, getters) {
        return !getters.fullservePlus && !getters.fromDPC && (!!getters.asFullserve || getters.openStateClient)
    },
    paying_employee_or_spouse (state, getters) {
        return !!getters.groupPaying && (getters.client.group_relationship === 'EMPLOYEE_SPOUSE' || getters.client.group_relationship === 'EMPLOYEE')
    },
    cms564Assist (state, getters) {
        return !!getters.client?.group?.cms564_assist
    },
    ichra (state) {
        return state.survey?.ichra
    },
    surveyRoutes (state, getters) {
        // const client = getters.client
        const id = getters.uid
        const isConsultationScripts = getters.activeRoutePath.split('/').pop() === 'consultation-scripts'
        // `originalStateSoa60days` and `isFullserve` used to gate entries in the
        // client list below; that list now derives its gating from the wizard
        // config's own `when` predicates, so neither is read here any more.
        // const edge = getters.edge
        let list = []
        if ((id || isConsultationScripts) && getters.canAgent) {
            list = [
                { path: `/survey/${id}/`, text: 'Profile', icon: 'user', nav: true, label: true },

                { path: `/survey/${id}/demographics`, text: 'Demographics', nav: true, icon: 'clipboard-user' },
                { path: `/survey/${id}/enrollment-period`, text: 'Enrollment Period', nav: true, icon: 'calendar-plus' },
                { path: `/survey/${id}/income-surcharges`, text: 'Income Surcharges', nav: true, icon: 'sack-dollar' },
                { path: `/survey/${id}/health-lifestyle`, text: 'Health/Lifestyle', nav: true, icon: 'heartbeat' },

                // Doctors and hospitals are one page since #656 comment 2;
                // `/survey/{id}/hospitals` is a redirect stub now, and a nav
                // row for it would sit between Pharmacies and Plans in this
                // index-driven list and make Next from Pharmacies land on it.
                { path: `/survey/${id}/providers`, text: 'Doctors & Hospitals', icon: 'user-md', nav: true, label: false },
                { path: `/survey/${id}/drugs`, text: 'Drugs', icon: 'prescription-bottle-alt', nav: true, label: false },
                { path: `/survey/${id}/pharmacies`, text: 'Pharmacies', icon: 'store', nav: true, label: false },
                { path: `/survey/${id}/plans`, text: 'Plans', icon: 'list', nav: true, label: false },
                { path: `/survey/${id}/summary`, text: 'Summary', icon: 'file-contract', nav: true, label: false },
                // { path: `/survey/${id}/aep`, text: 'AEP', icon: 'cauldron', nav: false, label: false },
                { path: `/survey/${id}/quick-aep`, text: 'Quick AEP', nav: true, icon: 'cauldron', label: false },
                { path: `/survey/${id}/soa`, text: 'SOAs', nav: true, icon: 'pen', label: false },

                // Activity and Wizard at the far right (2026-10-05)
                { path: `/survey/${id}/activity`, query: { strict: false, activity: 'send_summary' }, text: 'Activity', icon: 'chart-line', nav: true, label: false },
                { path: `/survey/${id}/step-by-step`, text: 'Wizard', icon: 'hat-wizard', nav: true, label: false },
            ]
        } else {
            // Client-facing nav. Derived from the checkup wizard's own step list
            // (data/checkup-config.js) rather than hand-maintained, so the nav
            // and the wizard cannot drift: whatever steps this client actually
            // walks are exactly the links they get. That is also why `plans`
            // appears only for selfserve clients without a special case here —
            // the step itself carries `when: g => g.selfserve`.
            //
            // The old hardcoded list pointed at /survey/* and had grown its own
            // gating (`isFullserve ? Start : null`, `isFullserve && !soa60days
            // ? Signature : null`, `isFullserve ? Complete : null`) that no
            // longer matched what the wizard shows — and it was missing
            // Hospitals entirely, which has been a checkup step since #598.
            // The /survey/* paths themselves still resolve: they are
            // redirectors to their /checkup/* counterparts
            // (util/checkup-redirect.js), so live email links keep working.
            list = [
                // `nav: false` on a step keeps it out of the nav while leaving
                // it a real step — see data/checkup-config.js's `done`.
                ...CHECKUP_WIZARD.stepsFor(getters)
                    .filter(step => step.nav !== false)
                    .map(step => ({
                        path: step.path,
                        text: step.label,
                        icon: step.icon,
                        nav: true,
                    })),
            ]
        }
        return list.filter(item => item)
    },
    surveyRoutesMap (state, getters) {
        return _.keyBy(getters.surveyRoutes, (sr) => {
            return sr.path.split('/').pop()
        })
    },
    surveyRoutesPathsMap (state, getters) {
        return _.keyBy(getters.surveyRoutes, (sr) => {
            return sr.path.replace(/\/$/, '')
        })
    },
    surveyRoutesNavItems (state, getters) {
        // todo: this could cause potential problems since we're using the surveyRoutesNavItems array for index navigation
        // hiding nav=false for now
        // return getters.surveyRoutes.filter(sr => sr.nav)
        return getters.surveyRoutes
    },
    isSurveyRoute (state, getters) {
        return !!getters.surveyRoutesPathsMap[getters.activeRoutePath.replace(/\/$/, '')]
    },
    surveyRouteIndex (state, getters) {
        return (route) => {
            return _.findIndex(getters.surveyRoutesNavItems, surveyRoute => surveyRoute.path === route?.path)
        }
    },
    currentSurveyRouteIndex (state, getters) {
        if (!getters.isSurveyRoute) {
            return -1
        }
        return getters.surveyRouteIndex(getters.activeRoute)
    },
    previousSurveyRoute (state, getters) {
        return getters.surveyRoutesNavItems[getters.currentSurveyRouteIndex - 1]
    },
    currentSurveyRoute (state, getters) {
        return getters.surveyRoutesNavItems[getters.currentSurveyRouteIndex]
    },
    firstSurveyRoute (state, getters) {
        return getters.surveyRoutesNavItems[0]
    },
    lastSurveyRoute (state, getters) {
        return getters.surveyRoutesNavItems[getters.surveyRoutesNavItems.length - 1]
    },
    nextSurveyRoute (state, getters) {
        return getters.surveyRoutesNavItems[getters.currentSurveyRouteIndex + 1]
    },
    activeRoute (state, getters) {
        return getters.route
    },
    activeRoutePath (state, getters) {
        return getters.activeRoute?.path
    },
    params (state, getters) {
        return getters.route.params
    },
    query (state, getters) {
        return getters.route.query
    },
    hostUrl (state, getters) {
        return state.host_url || state.host_url || `${getters.location.protocol}://${getters.location.hostname}`
    },
    effectiveHostUrl (state, getters) {
        return state.effective_host_url || state.host_url || `${getters.location.protocol}://${getters.location.hostname}`
    },
    route (state) {
        if (typeof window !== 'undefined') {
            return window.$nuxt.$route
        }
        return state.route || { query: {}, params: {}, path: '' }
    },
    location (state, getters) {
        return getters.window?.location || {}
    },
    // don't like this when isServer
    window () {
        return typeof window === 'undefined' ? global : window
    },
    lightTheme (state, getters) {
        // [todo@aziz] change this to a stored value?
        return true
    },
    darkTheme (state, getters) {
        return !getters.lightTheme
    },
    crmBase (state) {
        return state.crmBase
    },
    autosave (state) {
        return state.autosave
    },
    routeUid (state, getters) {
        // todo: risky?
        return getters.query?.id || getters.params?.uid
    },
    uid (state, getters) {
        if (getters.canAgent) {
            return getters.routeUid || state.survey?.id || state.summary?.user_id
        }
        return getters.routeUid
    },
    slug (state, getters) {
        if (getters.canAgent) {
            return getters.query?.slug || getters.params?.slug
        }
        return getters.group?.slug
    },
    edge (state, getters) {
        return getters.user?.edge
    },
    year (state, getters) {
        return parseInt(state.year || getters.query?.year, 10) || getters.defaultYear
    },
    // The plan year a page opens on when nobody chose one (no ?year=, nothing
    // picked in the header). Oracle publishes next year's constants around
    // Oct 1, ahead of that year's plan scrape; the newest year takes over from
    // Oct 1 (isAfterOct1). Either way it is only
    // ever a year oracle supports: before oracle's list has loaded it is the
    // calendar year, never `years`' guessed next year.
    defaultYear (state, getters) {
        const supported = getters.oracleYears
        if (!supported.length) {
            return getters.thisYear
        }
        const newest = Math.max(...supported)
        if (getters.isAfterOct1 || !supported.includes(getters.thisYear)) {
            return newest
        }
        return getters.thisYear
    },
    // The years oracle serves constants for, i.e. the ones Flow can quote.
    // Empty until getSurvey or loadYears has run; unlike `years`, never padded
    // with a guess.
    oracleYears (state) {
        return Object.keys(state.years || {}).map(year => parseInt(year, 10))
    },
    years (state, getters) {
        const years = Object.keys(state.years || {}).map(year => parseInt(year))
        if (years.length) {
            return years
        }
        return [
            getters.lastYear,
            getters.thisYear,
            ...(getters.hasPreAEPStarted ? [getters.nextYear] : []),
        ]
    },
    latestYear (state, getters) {
        const years = _.sortBy(getters.years, y => y)
        return years[years.length - 1]
    },
    lastYear (state, getters) {
        return getters.thisYear - 1
    },
    thisYear () {
        return (new Date()).getFullYear()
    },
    nextYear (state, getters) {
        return getters.thisYear + 1
    },
    yearMinus2 (state, getters) {
        return getters.year - 2
    },
    yearsOptions () {
        return getters.years.map(year => ({ text: year, value: year }))
    },
    parseError () {
        return (e) => {
            if (e.parsed) {
                return e
            }
            e.parsed = true
            if (_.isArray(e?.response?.data?.detail)) {
                const errors = _.map(e.response.data.detail, (suberror) => {
                    // Remove body / query prefix, and __root__ if it's a top level error
                    let loc = _.slice(suberror.loc, 1)
                    if (loc[0] === '__root__') loc = _.slice(loc, 1)
                    let msg = suberror.msg
                    if (loc.length) msg = `${loc.join('.')}: ${msg}`
                    return msg
                })
                e.message = errors.join('\n')
            } else {
                e.message = e?.response?.data?.detail || e?.response?.data?.message || e?.message
            }
            if (/duplicate key/i.test(e.message)) {
                e.message = 'We already have a profile with this email address and date of birth'
            }
            return e
        }
    },
    isNotAEP (state, getters) {
        return !getters.isAEP
    },
    isPreAEP (state, getters) {
        return getters.isBetweenOct1Dec6
    },
    isAepSurveyCycle (state, getters) {
        return getters.isAEPScheduling
    },
    canAepSurvey (state, getters) {
        return !!(getters.isAepSurveyCycle || getters.fullserve)
    },
    hasPreAEPStarted (state, getters) {
        return getters.isAfterOct1
    },
    isAEP (state, getters) {
        return getters.isBetweenOct15Dec7
    },
    isOEP (state, getters) {
        return getters.isBetweenJan1Mar31
    },
    isAEPScheduling (state, getters) {
        return getters.isBetweenSep1Dec3 || !!getters.route.query.asaepscheduling
    },
    isOEPScheduling (state, getters) {
        return getters.isBetweenJan1Mar31
    },
    onLargeEmployerCoverage (state, getters) {
        return state.survey?.pre_medicare_coverage === 'EMPLOYER' && ((getters.survey?.disabled && getters.survey?.employer_size_gt100) || getters.survey?.employer_size_gt20)
    },
    isSEP (state, getters) {
        return getters.isSEPLEC || getters.isSEPMoving
    },
    isSEPLEC (state, getters) {
        return state.survey?.enrollment_period === 'SEP_LEC' || ((getters.dobx.t65 || getters.dobx.gte65) && getters.onLargeEmployerCoverage && state.survey?.working_p65 && state.survey?.employer_coverage_p65)
    },
    isSEPMoving (state, getters) {
        return state.survey?.enrollment_period === 'SEP_MOVING' || (getters.hasMedicare && getters.hasSuppOrMapd && state.survey?.moving)
    },
    isIEP (state, getters) {
        return state.survey?.enrollment_period === 'IEP' || getters.dobx.lt65
    },
    lt62 (state, getters) {
        return getters.dobx.lt62
    },
    lt65 (state, getters) {
        return getters.dobx.lt65
    },
    gte65 () {
        return getters.dobx.lt65
    },
    ageGroup (state, getters) {
        if (!getters.dobx?.age) {
            return null
        }
        if (getters.dobx.lt65) {
            return 'UNDER_65'
        } else if (getters.dobx.t65) {
            return 'TURNING_65'
        } else {
            return 'OVER_65'
        }
    },
    hasPartA (state) {
        return state.survey?.part_a_date && moment(state.survey?.part_a_date).isBefore(moment())
    },
    hasPartB (state) {
        return !!state.survey?.part_b_date && moment(state.survey?.part_b_date).isBefore(moment())
    },
    hasMedicare (state, getters) {
        return getters.hasPartA || getters.hasPartB
    },
    hasSuppOrMapd (state) {
        return state.survey?.current_medicare_coverage === 'SUPP' || state.survey?.current_medicare_coverage === 'MAPD' || state.survey?.current_medicare_coverage === 'SUPP_PDP'
    },
    csnpConditions (state, getters) {
        const conditions = (state.survey?.possible_csnp_conditions || [])
        if (!conditions?.filter) {
            return []
        }
        return conditions.filter(c => c.slug && state.survey && state.survey[c.slug])
    },
    possibleCsnpConditions (state, getters) {
        const conditions = (state.survey?.possible_csnp_conditions || [])
        if (!conditions?.filter) {
            return []
        }
        // if condition.slug == cardiovascular_disorders is in there, remove chronic_heart_failure so we won't show both
        if (_.find(conditions, { slug: 'cardiovascular_disorders' })) {
            return conditions.filter(c => c.slug !== 'chronic_heart_failure')
        }
        return conditions
    },
    drugsByRxcui (state, getters) {
        return _.keyBy(state.survey?.drugs || [], 'rxcui')
    },
    isBetweenSep1Dec31 () {
        return moment().isBetween(moment().set({ month: 8, date: 1 }).startOf('day'), moment().set({ month: 11, date: 31 }).endOf('day'))
    },
    isBetweenAug15Sep30 () {
        return moment().isBetween(moment().set({ month: 7, date: 15 }).startOf('day'), moment().set({ month: 8, date: 30 }).endOf('day'))
    },
    isBetweenAug15Dec6 () {
        return moment().isBetween(moment().set({ month: 7, date: 15 }).startOf('day'), moment().set({ month: 11, date: 6 }).endOf('day'))
    },
    isBetweenAug15Oct12 () {
        return moment().isBetween(moment().set({ month: 7, date: 15 }).startOf('day'), moment().set({ month: 9, date: 12 }).endOf('day'))
    },
    isBetweenSep1Oct12 () {
        return moment().isBetween(moment().set({ month: 8, date: 1 }).startOf('day'), moment().set({ month: 9, date: 12 }).endOf('day'))
    },
    isBetweenSep1Dec3 () {
        return moment().isBetween(moment().set({ month: 8, date: 1 }).startOf('day'), moment().set({ month: 11, date: 3 }).endOf('day'))
    },
    isBetweenSep1Dec7 () {
        return moment().isBetween(moment().set({ month: 8, date: 1 }).startOf('day'), moment().set({ month: 11, date: 7 }).endOf('day'))
    },
    isBetweenOct15Dec7 () {
        return moment().isBetween(moment().set({ month: 9, date: 15 }).startOf('day'), moment().set({ month: 11, date: 7 }).endOf('day'))
    },
    isBetweenOct1Dec6 () {
        return moment().isBetween(moment().set({ month: 9, date: 1 }).startOf('day'), moment().set({ month: 11, date: 6 }).endOf('day'))
    },
    isBetweenOct1Dec5 () {
        return moment().isBetween(moment().set({ month: 9, date: 1 }).startOf('day'), moment().set({ month: 11, date: 5 }).endOf('day'))
    },
    isAfterOct1 () {
        // we need a day to scrape the data
        return moment().isAfter(moment().set({ month: 9, date: 1 }).startOf('day'))
    },
    isAfterOct3 () {
        return moment().isAfter(moment().set({ month: 9, date: 3 }).startOf('day'))
    },
    isBetweenJan1Mar31 () {
        return moment().isBetween(moment().set({ month: 0, date: 1 }).startOf('day'), moment().set({ month: 2, date: 31 }).endOf('day'))
    },
    isBetweenOct1Nov30 () {
        return moment().isBetween(moment().set({ month: 9, date: 1 }).startOf('day'), moment().set({ month: 10, date: 30 }).endOf('day'))
    },
    partAPremiumLt30 (state, getters) {
        return _.get(state, `years.${getters.year}.part_a_premiums.QUARTERS_LT_30`)
    },
    partAPremiumGte30Lte39 (state, getters) {
        return _.get(state, `years.${getters.year}.part_a_premiums.QUARTERS_30_39`)
    },
    partAPremiumGt40 (state, getters) {
        return _.get(state, `years.${getters.year}.part_a_premiums.QUARTERS_40`)
    },
    partAPremium (state, getters) {
        return _.get(state, `years.${getters.year}.part_a_premiums.${state.survey?.tax_quarters_paid}`) || 0
    },
    partADeductible (state, getters) {
        return _.get(state, `years.${getters.year}.part_a_deductible`)
    },
    partBPremium (state, getters) {
        return _.get(state, `years.${getters.year}.part_b_premium`)
    },
    partBDeductible (state, getters) {
        return _.get(state, `years.${getters.year}.part_b_deductible`)
    },
    partDGapEntrance (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_gap_entrance`)
    },
    partDGapBrandDrugPercent (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_gap_brand_drug_percent`)
    },
    partDGapGenericDrugPercent (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_gap_generic_drug_percent`)
    },
    partDCatastrophicEntrance (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_catastrophic_entrance`)
    },
    partDCatastrophicCoveredDrugPercent (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_catastrophic_covered_drug_percent`)
    },
    partDCatastrophicGenericDrugCopay (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_catastrophic_generic_drug_copay`)
    },
    partDCatastrophicBrandDrugCopay (state, getters) {
        return _.get(state, `years.${getters.year}.part_d_catastrophic_brand_drug_copay`)
    },
    partBDatePast6m (state) {
        if (!state.survey?.part_b_date) {
            return null
        }
        return moment().diff(moment(state.survey?.part_b_date), 'months') > 6
    },
    summaries (state) {
        return state.summaries || []
    },
    summariesMap (state) {
        return _.keyBy(state.summaries || [], 'id')
    },
    summaryAep (state) {
        return state.summary?.data?.config?.aep
    },
    summaryCards (state) {
        return _.uniq(state.summary?.data?.config?.cards || [])
    },
    summaryCardsMap (state, getters) {
        return _.keyBy(getters.summaryCards, c => c)
    },
    summaryMode (state) {
        return state.summary?.data?.config?.mode || null
    },
    summaryDataCards (state) {
        return state.summary?.data?.cards || {}
    },
    summarySurveyUpdated (state) {
        return state.summary?.data?.survey_updated
    },
    summaryManual (state) {
        return state.summary?.data?.manual
    },
    // ---- #721 AEP builder (spec §5.2) -----------------------------------------
    summaryRecommendation (state) {
        const rec = state.summary?.data?.config?.recommendation
        if (!rec) { return null }
        return { ...aep.DEFAULT_RECOMMENDATION, ...rec, current: { ...aep.DEFAULT_RECOMMENDATION.current, ...(rec.current || {}) } }
    },
    summaryCardsManual (state) {
        return state.summary?.data?.config?.cards_manual || {}
    },
    // D1: payer = fullservePlus; everyone else gets the non-payer wording.
    aepPayer (state, getters) {
        return !!getters.fullservePlus
    },
    aepCoverage (state, getters) {
        const c = getters.summaryRecommendation?.coverage || state.survey?.current_medicare_coverage
        return aep.COVERAGES.includes(c) ? c : null
    },
    aepCurrentResolved (state) {
        return state.aepCurrentResolved
    },
    // Spec 2026-09-28 §5.1: the resolver's id when that plan is in the quotes,
    // else the name matcher, else null. `merged` marks a consolidated plan.
    aepCurrentGuess (state, getters) {
        const byName = guessCurrentPlans(state.survey, getters.plans)
        const lines = getters.aepCurrentResolved?.lines || {}
        // review I1: only the coverage's lines, like the name matcher; a stale
        // id on another line (coverage changed on quick-aep) is not a current plan.
        const coverage = getters.aepCoverage || state.survey?.current_medicare_coverage
        const kinds = LINES_BY_COVERAGE[coverage] || []
        const out = { supp: null, pdp: null, mapd: null }
        kinds.forEach((kind) => {
            const line = lines[kind]
            const quote = line?.resolved_id != null ? getters.plansMap[line.resolved_id] : null
            out[kind] = quote ? { id: quote.id, confidence: 'id', quote, merged: line.status === 'consolidated' } : byName[kind]
        })
        return out
    },
    // The client's current plan ids: the verified recommendation's, else the
    // guess's (util/summary-plans.js). Shared by the summary sidebar and the
    // plan card's "current plan" badge.
    aepCurrentPlanIds (state, getters) {
        return currentPlanIds(getters.summaryRecommendation, getters.aepCurrentGuess)
    },
    // Why a line has no guess, with what the survey holds, for the builder row.
    aepCurrentUnresolved (state, getters) {
        return (kind) => {
            if (getters.aepCurrentGuess?.[kind]) { return null }
            const survey = state.survey || {}
            const carrier = survey[`${kind}_carrier`] || ''
            const name = survey[`${kind}_name`] || ''
            if (!carrier && !name && survey[`${kind}_plan_id`] == null) { return null }
            const line = getters.aepCurrentResolved?.lines?.[kind]
            let reason = 'not_identified'
            if (line?.status === 'terminated') {
                reason = 'terminated'
            } else if (line?.status === 'consolidated') {
                reason = 'consolidated_missing'
            } else if (line?.resolved_id != null) {
                reason = 'not_offered'
            }
            return { reason, carrier, name }
        }
    },
    aepProviderStatus (state, getters) {
        return (provider, plan) => (getters.isProviderInNetwork(provider, plan) ? 'in' : getters.isProviderUnsureNetwork(provider, plan) ? 'unsure' : 'out')
    },
    // The engine plan shape (plan doc Task 1). Money fields are coerced the
    // way sumMoney does, so a string on the wire cannot corrupt a total (#658).
    toEnginePlan (state, getters) {
        return (plan) => {
            if (!plan) { return null }
            const type = plan.mapd_or_ma || plan.type === 'ma' || plan.plan_type === 'ma' ? 'mapd' : plan.type
            return {
                id: plan.id,
                name: plan.name,
                carrier: plan.carrier,
                type,
                monthly_premium: toMoneyNumber(plan.monthly_premium) || 0,
                drug_deductible: toMoneyNumber(type === 'pdp' ? plan.drug_deductible : plan.drug_deductible_cost_share) || 0,
                drug_deductible_applicable_tiers: plan.drug_deductible_applicable_tiers || [],
                drug_deductible_all_tiers: !!plan.drug_deductible_all_tiers,
                medical_deductible: toMoneyNumber(plan.deductible_cost_share) || 0,
                moop: toMoneyNumber(plan.maximum_oopc_in_network || plan.maximum_oopc_cost_share) || 0,
                mapd_type: plan.mapd_type || null,
                letter: aep.planLetter(plan),
                household_discount: !!plan.household_discount,
                // the pharmacy the copay table shows, not the first one listed
                yearly_drug_cost: toMoneyNumber(plan.cheapest_pharmacy?.estimated_yearly_total) || 0,
                pharmacy_name: plan.cheapest_pharmacy?.pharmacy?.name || null,
                pharmacy_mail_order: !!plan.cheapest_pharmacy?.mail_order,
                pharmacy_period: tablePeriod(plan.cheapest_pharmacy),
                commissionable: getters.isPlanCommissionable(plan),
                enrollable: getters.isPlanEnrollable(plan),
                carrier_self: aep.carrierSelfComponent(plan, state.survey?.state),
                in_network_providers: plan.in_network_providers,
                out_network_providers: plan.out_network_providers,
                benefits_doc: getters.planBenefitsDocLinked(plan),
            }
        }
    },
    aepEnginePlansById (state, getters) {
        return _.mapValues(getters.summaryPlansMap, p => getters.toEnginePlan(p))
    },
    // Current plans resolve through the SUMMARY plans (they must be in the
    // Plans list to be priced and to form the combo); unverified => nothing.
    aepCurrent (state, getters) {
        const cur = { supp: null, pdp: null, mapd: null }
        const rec = getters.summaryRecommendation
        if (!rec?.current?.verified) { return cur }
        const byId = getters.aepEnginePlansById
        cur.supp = byId[rec.current.supp_id] || null
        cur.pdp = byId[rec.current.pdp_id] || null
        cur.mapd = byId[rec.current.mapd_id] || null
        return cur
    },
    aepContext (state, getters) {
        const rec = getters.summaryRecommendation
        if (!getters.summaryAep || !rec || !rec.current?.verified) { return null }
        return aep.buildContext({
            year: getters.year,
            payer: getters.aepPayer,
            coverage: getters.aepCoverage,
            rec,
            current: getters.aepCurrent,
            plansById: getters.aepEnginePlansById,
            providers: getters.providers,
            providerStatus: getters.aepProviderStatus,
            excludedIds: _.get(state.summary, 'data.cards.step_actions_enroll_plan.config.value.excluded') || [],
            currentNotices: getters.aepCurrentNotices,
        })
    },
    // Dien 2026-09-29 (3, 4): the resolver's terminated / merged verdicts for
    // the coverage's lines, with the carrier and name on file, for the letter.
    aepCurrentNotices (state, getters) {
        // Saved with the verified ids (AepBuilder setCurrent / verifyCurrent):
        // the client page, the PDF and the email never call the agent-only
        // resolver, so the letter must not depend on it (browser QA 2026-09-29).
        const rec = getters.summaryRecommendation
        if (rec?.current?.verified && Array.isArray(rec.current.notices)) { return rec.current.notices }
        const lines = getters.aepCurrentResolved?.lines || {}
        const kinds = LINES_BY_COVERAGE[getters.aepCoverage] || []
        return kinds.map((kind) => {
            const line = lines[kind]
            if (!line || (line.status !== 'terminated' && line.status !== 'consolidated')) { return null }
            return { kind, status: line.status, carrier: state.survey?.[`${kind}_carrier`] || '', name: state.survey?.[`${kind}_name`] || '', resolvedName: line.resolved_name || null }
        }).filter(Boolean)
    },
    aepLines (state, getters) {
        return getters.aepContext?.lines || []
    },
    aepActive (state, getters) {
        return getters.aepLines.length > 0
    },
    // MAPD -> MAPD: the Compare Plans card's columns as store plans, current
    // MAPD first (aep.mapdCompareColumns, 2026-10-07)
    aepMapdCompare (state, getters) {
        return aep.mapdCompareColumns(getters.aepContext).map(p => getters.summaryPlansMap[p.id]).filter(Boolean)
    },
    aepResultingPlans (state, getters) {
        return getters.aepLines.map(l => getters.summaryPlansMap[l.plan.id]).filter(Boolean)
    },
    // Round-2 spec §13.3: the options' resulting store plans, primary first,
    // and their combo card keys.
    aepOptionCombos (state, getters) {
        if (!getters.aepActive) { return [] }
        return aep.options(getters.aepContext).map(o => o.resulting.map(p => getters.summaryPlansMap[p.id]).filter(Boolean))
    },
    aepOptionKeys (state, getters) {
        return getters.aepActive ? aep.options(getters.aepContext).map(o => aep.comboKey(o.lines)) : []
    },
    aepAutoCards (state, getters) {
        return getters.aepContext ? aep.autoCards(getters.aepContext) : {}
    },
    userGroupPlans (state) {
        return state.userGroupPlans || []
    },
    userGroupPlansMap (state) {
        return _.keyBy(state.userGroupPlans, 'id')
    },
    summaryUserGroupPlans (state, getters) {
        const userGroupPlans = {}
        _.each(state.summary?.data?.user_group_plans || {}, (p, k) => {
            if (getters.userGroupPlansMap[p.id]) {
                const userGroupPlan = _.cloneDeep(getters.userGroupPlansMap[p.id])
                userGroupPlans[k] = getters.parsePlan(userGroupPlan)
            }
        })
        return userGroupPlans
    },
    summaryUserGroupPlansMap (state, getters) {
        return getters.summaryUserGroupPlans
    },
    summaryUserGroupPlansList (state, getters) {
        return _.values(getters.summaryUserGroupPlansMap || {})
    },
    groupPlans (state) {
        return state.groupPlans || []
    },
    groupPlansMap (state) {
        return _.keyBy(state.groupPlans, 'id')
    },
    summaryPlans (state, getters) {
        const plans = {}
        const plansMap = getters.plansMap
        _.each(state.summary?.data?.plans || {}, (p, k) => {
            if (plansMap[p.id]) {
                const plan = getters.parsePlan(_.cloneDeep(plansMap[p.id]))
                plan.recommendation = p.recommendation
                plans[k] = plan
                return plan
            }
            // console.warn(`Summary plan id:${p.id} was not found in the most recent quotes pulled, possibly wrong year or wrong plan_id?`)
        })
        return plans
    },
    summaryPlansMap (state, getters) {
        return getters.summaryPlans
    },
    summaryPlansList (state, getters) {
        const plans = _.values(getters.summaryPlansMap || {})
        plans.sort((p1, p2) => {
            if (_.isNil(p1.recommendation)) {
                return 0
            }
            if (_.isNil(p2.recommendation)) {
                return -1
            }
            return p1.recommendation < p2.recommendation ? -1 : 1
        })
        return plans
    },
    summaryMapdPlans (state, getters) {
        return getters.summaryPlansList.filter(p => p.type === 'mapd' || p.type === 'ma')
    },
    summarySuppPlans (state, getters) {
        return getters.summaryPlansList.filter(p => p.type === 'supp')
    },
    summaryPdpPlans (state, getters) {
        return getters.summaryPlansList.filter(p => p.type === 'pdp')
    },
    summarySuppPdpPlans (state, getters) {
        return getters.summaryPlansList.filter(p => p.type === 'pdp' || p.type === 'supp')
    },
    summaryCombosMapAndList (state, getters) {
        // #721: with a verified recommendation the combos are exactly the
        // options' resulting plans (supp -> pdp, or mapd), one combo per
        // option (round-2 spec §13.3), never derived from the list.
        if (getters.summaryAep && getters.aepActive) {
            const combos = getters.aepOptionCombos
            const combosMap = {}
            combos.forEach((combo) => {
                combo.forEach((p) => { combosMap[p.id] = combo })
                combosMap[combo.map(p => p.id).join('_')] = combo
            })
            return { combos, combosMap, flat: _.uniqBy(_.flatten(combos), 'id') }
        }
        const plans = getters.summaryPlansList
        const combos = []
        const combosMap = {}
        const flat = []
        plans.forEach((plan) => {
            if (combosMap[plan.id]) {
                return
            }
            let combo = []
            if (plan.type === 'mapd' || plan.type === 'ma') {
                combo = [plan]
            } else if (plan.type === 'supp') {
                const pdp = plans.filter(p => p.type === 'pdp')[0]
                combo = [plan, pdp].filter(p => p)
            } else if (plan.type === 'pdp') {
                const supp = plans.filter(p => p.type === 'supp')[0]
                combo = [supp, plan].filter(p => p)
            }
            if (getters.summaryAep) {
                combo = getters.modeFilterSummaryPlans(combo)
            }
            combo.forEach((p) => {
                combosMap[p.id] = combo
                flat.push(p)
            })
            if (combo.length) {
                combosMap[combo.map(p => p.id).join('_')] = combo
                combos.push(combo)
            }
        })
        return { combos, combosMap, flat }
    },
    summaryCombos (state, getters) {
        return getters.summaryCombosMapAndList.combos
    },
    summaryCombosMap (state, getters) {
        return getters.summaryCombosMapAndList.combosMap
    },
    modeFilteredSummaryPlans (state, getters) {
        return getters.summaryCombosMapAndList.flat
    },
    modeFilterSummaryPlans (state, getters) {
        return (plans) => {
            if (getters.summaryMode === 'SWITCH_PDP' || getters.summaryMode === 'STAY_PDP') {
                return plans.filter(p => p.pdp)
            }
            if (getters.summaryMode === 'SWITCH_MAPD') {
                return plans.filter(p => p.mapd_or_ma)
            }
            if (getters.summaryMode === 'SWITCH_SUPP') {
                return plans.filter(p => p.supp)
            }
            if (getters.summaryMode === 'SWITCH_SUPP_PDP' || getters.summaryMode === 'STAY_SUPP_PDP') {
                return plans.filter(p => p.supp || p.pdp)
            }
            if (getters.summaryMode === 'STAY_MAPD') {
                return plans.filter(p => p.mapd_or_ma)
            }
            return plans
        }
    },
    summaryCombosOptions (state, getters) {
        const options = _.uniqBy([].concat(getters.summaryCombos.map((combo) => {
            return {
                value: `combo_${combo.map(p => p.id).join('_')}`,
                text: combo.map(p => p.name).join(' + '),
                autoCondition: true,
                plan_type: combo.map(p => p.type).join(' + '),
            }
        })), 'value')
        return options
    },
    hasMapd (state, getters) {
        return !!getters.summaryMapdPlans.length
    },
    hasSupp (state, getters) {
        return !!getters.summarySuppPlans.length
    },
    hasPdp (state, getters) {
        return !!getters.summaryPdpPlans.length
    },
    hasAllPlanTypes (state, getters) {
        return getters.hasMapd && getters.hasSupp && getters.hasPdp
    },
    missingTypes (state, getters) {
        return [
            getters.hasNotMapd ? 'mapd' : null,
            getters.hasNotSupp ? 'supp' : null,
            getters.hasNotPdp ? 'pdp' : null,
        ].filter(t => t)
    },
    allowedTypes (state, getters) {
        return _.uniq(getters.missingTypes.concat(getters.summaryAep && getters.hasNotMin2Ppds ? ['pdp'] : []))
    },
    hasNotMapd (state, getters) {
        return !getters.summaryMapdPlans.length
    },
    hasNotSupp (state, getters) {
        return !getters.summarySuppPlans.length
    },
    hasNotPdp (state, getters) {
        return !getters.summaryPdpPlans.length
    },
    hasMin2Ppds (state, getters) {
        return getters.summaryPdpPlans.length > 1
    },
    hasNotMin2Ppds (state, getters) {
        return true
        // return !getters.hasMin2Ppds
    },
    summaryRecommendedPlans (state, getters) {
        return getters.summaryPlansList.filter(p => _.isNumber(p.recommendation))
    },
    yearSummaryId (state, getters) {
        return _.find(getters.summaries || [], summary => summary.year === getters.year && !summary.auto)?.id
    },
    summaryMerge () {
        return (data) => {
            // A copy per summary: a new summary (data {}) took these cards,
            // plans and config.cards by reference, so a card edited on one
            // client's summary showed and saved on the next one opened in the
            // tab, drug table and doctors included (Allison, client 5349).
            const defaults = _.cloneDeep(DEFAULT_SUMMARY_DATA)
            return {
                ...defaults,
                ...(data || {}),
                ...{
                    config: {
                        ...defaults.config,
                        ...((data || {}).config || {})
                    }
                },
            }
        }
    },
    providersMap (state) {
        return _.keyBy(state.survey?.providers || [], 'npi')
    },
    pharmaciesMap (state) {
        return _.keyBy(state.survey?.pharmacies || [], 'npi')
    },
    packagesMap (state, getters) {
        return (state.survey?.drugs || []).reduce((map, drug) => {
            const drg = getters.parseDrug(_.cloneDeep(drug))
            map[drg?.package?.ndc] = {
                ...(drg.package || {}),
                dosage: drg.dosage ? { ...drg.dosage } : null,
                custom_dosage: drg.custom_dosage,
                custom_package: drg.custom_package,
                drug: { ...drg }
            }
            return map
        }, {})
    },
    dosageLabel () {
        return (dsg) => {
            return `${dsg.strength.toLowerCase()}${dsg.strength_units.toLowerCase()} ${_.lowerCase(dsg.form)}${dsg.name ? ` (${dsg.name})` : ''}`
        }
    },
    packageLabel () {
        return (pkg, dsg) => {
            let label = ''
            if (pkg.unit === 'EA') {
                label = `${_.capitalize(pkg.package_type)} of ${pkg.size} ${_.lowerCase(dsg.form)}`
            } else {
                label = `${pkg.unit && pkg.unit !== 'N/A' ? `${_.lowerCase(pkg.unit)} ` : ''}${pkg.package_type ? _.lowerCase(pkg.package_type) : ''}`
                label = pkg.size ? `${pkg.size}${label}` : label
            }
            if (pkg.quantity > 1 && pkg.use_quantity) {
                label = `${pkg.quantity}x ${label}`
            }
            return `${label}`
        }
    },
    parsePackage (state, getters) {
        return (pkg, dosage) => {
            if (pkg.parsed) {
                return pkg
            }
            pkg.parsed = true
            pkg.text = pkg.text || getters.packageLabel(pkg, dosage)
            return pkg
        }
    },
    parseDosage (state, getters) {
        return (dsg, packages) => {
            if (dsg.parsed) {
                return dsg
            }
            dsg.text = dsg.text || getters.dosageLabel(dsg)
            dsg.hyphens = dsg.text.match(/-/g)?.length || 0
            dsg.packages = (dsg.packages || packages || []).map(pkg => getters.parsePackage(pkg, dsg))
            return dsg
        }
    },
    parseDrug (state, getters) {
        return (drg) => {
            if (drg.parsed) {
                return drg
            }
            drg.parsed = true
            drg.name = drg.name && drg.name.replace(/\n$/, '')
            drg.text = drg.name
            drg.dosage = drg.dosage ? getters.parseDosage(drg.dosage) : drg.dosage
            drg.package = drg.package && drg.dosage ? getters.parsePackage(drg.package, drg.dosage) : drg.package
            drg.dosages = (drg.dosages || []).map(dsg => getters.parseDosage(dsg))
            drg.dosages.forEach((dsg) => {
                if (dsg.hyphens) {
                    drg.max_hyphens = Math.max(dsg.hyphens, drg.max_hyphens || 0)
                }
            })
            drg.frequency_days = drg.frequency ? parseInt((drg.frequency.match(/(\d+)/) || [])[1], 10) || 30 : null
            drg.daily_average = drg.frequency_days ? drg.quantity / drg.frequency_days : null
            return drg
        }
    },
    effectiveTaxFilingStatus () {
        /* eslint-disable */
        return ({ tax_filing_status, lives_with_spouse }) => {
            if (tax_filing_status === 'MARRIED_SEPARATELY' && lives_with_spouse === false) {
                return 'SINGLE'
            }
            return tax_filing_status
        }
    },
    iramaaBThresholds (state, getters) {
        /* eslint-disable */
        return ({ year, tax_filing_status, lives_with_spouse }) => {
            const effective = getters.effectiveTaxFilingStatus({ tax_filing_status, lives_with_spouse })
            const list = _.cloneDeep(_.get(state, `years.${year}.irmaa_part_b_thresholds.${effective}`) || {})
            const keys = Object.keys(list)
            keys.unshift(0)
            return {
                keys,
                list
            }
        }
    },
    iramaaDThresholds (state, getters) {
        /* eslint-disable */
        return ({ year, tax_filing_status, lives_with_spouse }) => {
            const effective = getters.effectiveTaxFilingStatus({ tax_filing_status, lives_with_spouse })
            const list = _.cloneDeep(_.get(state, `years.${year}.irmaa_part_d_thresholds.${effective}`) || {})
            const keys = Object.keys(list)
            keys.unshift(0)
            return {
                keys,
                list
            }
        }
    },
    calcIrmaaB (state, getters) {
        /* eslint-disable */
        return ({ year, current_income, tax_filing_status, lives_with_spouse }) => {
            if (tax_filing_status === 'MARRIED_SEPARATELY' && lives_with_spouse == null) {
                return null
            }
            const thresholds = getters.iramaaBThresholds({ year, tax_filing_status, lives_with_spouse })
            const key = thresholds.keys[current_income]
            return thresholds.list[key] || 0
        }
    },
    irmaaB (state, getters) {
        return getters.calcIrmaaB({ year: getters.year, ...state.survey })
    },
    getProjectedIrmaaB (state, getters) {
        return () => getters.calcIrmaaB({ year: getters.year, current_income: state.survey?.projected_income, tax_filing_status: state.survey?.projected_tax_filing_status, lives_with_spouse: state.survey?.projected_lives_with_spouse })
    },
    projectedIncomeChange (state) {
        return state.survey?.projected_income !== state.survey?.current_income
    },
    projectedIncomeDecrease (state) {
        return state.survey?.projected_income < state.survey?.current_income
    },
    projectedIncomeIncrease (state) {
        return state.survey?.projected_income > state.survey?.current_income
    },
    projectedIncomeChange (state, getters) {
        return getters.projectedIncomeDecrease || getters.projectedIncomeIncrease
    },
    dob (state, getters) {
        return getters.client?.dob
    },
    dobx (state, getters) {
        return fromDob(getters.dob) || {}
    },
    lt65 (state, getters) {
        return getters.dobx.lt65
    },
    gte65 (state, getters) {
        return getters.dobx.gte65
    },
    calcIrmaaD (state, getters) {
        /* eslint-disable */
        return ({ year, current_income, tax_filing_status, lives_with_spouse }) => {
            if (tax_filing_status === 'MARRIED_SEPARATELY' && lives_with_spouse == null) {
                return null
            }
            const thresholds = getters.iramaaDThresholds({ year, current_income, tax_filing_status, lives_with_spouse })
            const key = thresholds.keys[current_income]
            return thresholds.list[key] || 0
        }
    },
    irmaaD (state, getters) {
        return getters.calcIrmaaD({ year: getters.year, ...state.survey })
    },
    getProjectedIrmaaD (state, getters) {
        return () => getters.calcIrmaaD({ year: getters.year, current_income: state.survey?.projected_income, tax_filing_status: state.survey?.projected_tax_filing_status, lives_with_spouse: state.survey?.projected_lives_with_spouse })
    },
    income (state, getters) {
        const effective = getters.effectiveTaxFilingStatus({ tax_filing_status: state.survey?.tax_filing_status, lives_with_spouse: state.survey?.lives_with_spouse })
        return (getters.incomeOptionsFiledAsMap[effective] || {})[state.survey?.current_income]?.text
    },
    projectedIncome (state, getters) {
        const effective = getters.effectiveTaxFilingStatus({ tax_filing_status: state.survey?.projected_tax_filing_status, lives_with_spouse: state.survey?.projected_lives_with_spouse })
        return (getters.incomeOptionsFiledAsMap[effective] || {})[state.survey?.projected_income]?.text
    },
    incomeOptionsFiledAs (state, getters) {
        return (filedAs, year = getters.year, lives_with_spouse) => {
            const effective = getters.effectiveTaxFilingStatus({ tax_filing_status: filedAs, lives_with_spouse })
            const keys = Object.keys(_.get(state, `years.${year}.irmaa_part_d_thresholds.${effective}`) || {}).map(k => parseInt(k))
            keys.sort((a1, a2) => a1 > a2 ? 1 : -1)
            return keys.reduce((arr, k, i) => {
                const previous = keys[i - 1]
                const amount = k
                let text, min, max
                if (!i) {
                    min = 0
                    max = amount - 1
                    text = `Less than ${money(amount, { minimumFractionDigits: 0 })}`
                    arr.push({ text, min, max })
                } else if (i === keys.length - 1) {
                    min = previous
                    max = amount - 1
                    text = `Between ${money(min, { minimumFractionDigits: 0 })} and ${money(max, { minimumFractionDigits: 0 })}`
                    arr.push({ text, min, max })
                    min = amount
                    max = null
                    text = `More than ${money(amount, { minimumFractionDigits: 0 })}`
                    arr.push({ text, min, max })
                } else {
                    min = previous
                    max = amount - 1
                    text = `Between ${money(min, { minimumFractionDigits: 0 })} and ${money(max, { minimumFractionDigits: 0 })}`
                    arr.push({ text, min, max })
                }
                return arr.map((bracket, index) => {
                    bracket.value = index
                    return bracket
                })
            }, [])
        }
    },
    incomeOptionsFiledAsMap (state, getters) {
        return getters.filings.reduce((map, filing) => {
            map[filing.value] = getters.incomeOptionsFiledAs(filing.value)
            return map
        }, {})
    },
    parseQuote (state, getters) {
        return getters.parsePlan
    },
    carriersReplace (state, getters) {
        return (str = '') => carrierDisplayName(str)
    },
    planNameReplace (state, getters) {
        // The override is resolved from `plan_extras` (the block /quotes2/
        // ships alongside the quotes), not from the plan row itself, so it is
        // passed in explicitly.
        return plan => planDisplayName(plan, getters.hasPlanPreferredName(plan))
    },
    planCarriersReplace (state, getters) {
        return (p) => {
            p.original_name = p.name
            p.name = getters.carriersReplace(getters.planNameReplace(p))
            // The catalog carrier, before the display rewrite: plan notes and
            // oracle's get_plan_notes match on it ("Humana (Humana Insurance
            // Company)", which displays as "Humana (Humana)").
            p.original_carrier = p.original_carrier || p.carrier
            p.carrier = getters.carriersReplace(p.carrier)
            return p
        }
    },
    parsePlan (state, getters) {
        return (plan) => {
            if (!plan || plan.plan_type === 'gp' || plan.parsed) {
                return plan
            }
            plan.parsed = true

            plan.eft_discount = 0
            plan.rules = plan.rules || []
            plan.rules.forEach(rule => {
                const match = rule.message.match(BENEFIT_COST_SHARE_RE)
                if (match?.groups?.dollars && /EFT/.test(rule.message)) {
                    plan.eft_discount = parseInt(match.groups.dollars.replace(/,/g, '')) || 0
                }
            })

            let categories
            if (_.isNil(plan.monthly_premium)) {
                plan.yearly_premium = (parseFloat(plan.annual_premium) || 0) + ((plan.eft_discount || 0) * 12)
                plan.monthly_premium = plan.yearly_premium / 12
            } else {
                plan.yearly_premium = plan.monthly_premium * 12
            }
            plan.effective_date_annual_premium = parseFloat(plan.effective_date_annual_premium || plan.yearly_premium || 0)

            const householdDiscountInfo = householdDiscount(plan, getters.survey?.household_discount)
            plan.household_discount = householdDiscountInfo?.percent ? `${householdDiscountInfo.percent}%` : null
            plan.household_discount_monthly_premium = householdDiscountInfo?.monthlyPremium ?? null
            plan.household_discount_available = !!householdDiscountInfo

            plan.drug_cost_by_pharmacy = plan.drug_cost_by_pharmacy || {}
            plan.drug_data_by_pharmacy = plan.drug_data_by_pharmacy || {}
            plan.drug_info = plan.drug_info || {}
            plan.factors = plan.factors || {}
            plan.deductible = plan.deductible || plan.plan_deductible || plan.part_b_deductible || ''
            plan.drug_cost_by_pharmacy_list = []
            plan.key = plan.id

            // const effectiveDate = getters.survey.effective_date ? moment(getters.survey.effective_date) : null
            function processPharmacyCosts (p, options = {}) {
                // // moved to backend
                // p.original_estimated_monthly_costs = _.cloneDeep(p.estimated_monthly_costs)
                // let d0
                // let monthsDiff = 0
                // if (effectiveDate && p.estimated_monthly_costs && p.estimated_monthly_costs[0]) {
                //     d0 = moment(p.estimated_monthly_costs[0].date)
                //     monthsDiff = effectiveDate.diff(d0, 'months')
                // }
                // if (monthsDiff > 0) {
                //     // const uptoMonth = 12 - monthsDiff
                //     // p.estimated_monthly_costs = p.estimated_monthly_costs.slice(0, uptoMonth)
                //     p.estimated_monthly_costs = p.estimated_monthly_costs.filter((mc, i) => {
                //         const m = moment(mc.date).add(monthsDiff, 'months')
                //         if (m.year() == getters.year) {
                //             mc.date = m.format('YYYY-MM-DD')
                //             return true
                //         } else {
                //             p.estimated_monthly_costs_per_drug_table.splice(i, 1)
                //             p.estimated_monthly_costs_per_drug_differences.splice(i, 1)
                //             return false
                //         }
                //     })
                //     // p.estimated_monthly_costs_per_drug_table = p.estimated_monthly_costs_per_drug_table.slice(0, uptoMonth)
                //     // p.estimated_monthly_costs_per_drug_differences = p.estimated_monthly_costs_per_drug_differences.slice(0, uptoMonth)
                //     if (!p.estimated_monthly_costs_shifted) {
                //         p.estimated_monthly_costs_shifted = true
                //     }
                //     // todo: fix drug_costs[] estimated yearly total
                // }
                // p.estimated_yearly_total = _.sumBy(p.estimated_monthly_costs, mc => mc.total)

                p.cost_yearly = p.estimated_yearly_total || p.cost || 0
                p.cost_monthy = p.cost_yearly / p.estimated_monthly_costs.length
                p.tier5s = []

                // p.drug_costs = _.sortBy(p.drug_costs || [], dc => dc.full_cost)

                p.drug_costs = p.drug_costs.map(dc => {
                    dc = getters.parseDrug(dc)
                    dc.package = getters.packagesMap[dc.ndc]
                    if (options.fix && dc.drug_info && dc.drug_info.tier === 5 && plan.mail_order_pharmacy) {
                        dc = _.clone(plan.mail_order_pharmacy.packages[dc.ndc])
                    } else {
                        dc.drug_info = plan.drug_info[dc.ndc]
                        dc.alternatives = getters.findDrugAlternatives(dc)
                        if (dc.drug_info.tier === 5) {
                            p.tier5s.push(dc)
                        }
                        if (options.add && dc.drug_info && dc.drug_info.tier === 5 && plan.mail_order_pharmacy) {
                            dc.mail_order_drug_cost = _.clone(plan.mail_order_pharmacy.packages[dc.ndc])
                        }
                    }

                    if (dc.coverage_reason === 'COVERED') {
                        dc.is_covered = true
                    }
                    dc.is_not_covered = !dc.is_covered
                    dc.does_count_towards_deductible = _.round(dc.deductible_cost, 1) !== _.round(dc.initial_cost, 1)
                    if (_.round(dc.deductible_cost, 1) === 0 && dc.full_cost > 0) {
                        dc.does_count_towards_deductible = false
                    }
                    dc.does_not_count_towards_deductible = !dc.does_count_towards_deductible
                    return dc
                })

                if (p.monthly_dates_processed) {
                    return
                }
                p.monthly_dates_processed = true

                const phases = p.phase_information || {}

                const pemc = p.estimated_monthly_costs
                // const pemc = moment.monthsShort().map((m, i)=> ({ date: `${getters.year}-${_.padStart(`${i + 1}`, 2, '0')}-01` }))

                p.monthly_dates = pemc

                p.estimated_yearly_total = options.fix ? _.sumBy(p.drug_costs, dc => dc.estimated_yearly_total) : p.estimated_yearly_total
                p.estimated_monthly_costs = options.fix ? p.estimated_monthly_costs.map((mc, i) => {
                    // subtract the monthly cost of the tier5 drug from this pharmacy, and add its monthly cost from the mail_order_pharmacy
                    // we have to do it for catastrophic, deductible, gap, initial and total
                    // TODO: HALP! we don't have per-drug-month-by-month values
                    return mc
                }) : p.estimated_monthly_costs

                p.estimated_monthly_costs_map = _.keyBy(p.estimated_monthly_costs, 'date')

                p.tier5sMap = _.keyBy(p.tier5s, 'ndc')
                p.packages = _.keyBy(p.drug_costs, 'ndc')
                p.drug_totals = {
                    full_cost: 0,
                    deductible_cost: 0,
                    initial_cost: 0,
                    gap_cost: 0,
                    catastrophic_cost: 0,
                }
                _.each(p.drug_totals, (val, key) => {
                    _.each(p.drug_costs, (cc) => {
                        p.drug_totals[key] += cc[key]
                    })
                })
            }

            // todo: why the f is medicare sending this wrong??
            // todo: track it down in oracle and fix it
            // if (plan?.drug_tiers?.tiers?.length) {
            //     const ts = plan?.drug_tiers?.tiers.slice(0)
            //     const t1 = ts.shift()
            //     ts.push(t1)
            //     plan.drug_tiers.tiers = ts
            // }

            _.each(plan.drug_data_by_pharmacy, (cost, npi) => {
                cost = !_.isNil(cost?.cost) ? cost.cost : cost || 0
                if (!_.isNumber(cost)) {
                    cost = parseFloat(cost) || 0
                }
                npi = npi || 'mail_order'
                const pharmacyData = plan.drug_data_by_pharmacy[npi] || plan.drug_data_by_pharmacy['']
                const pharmacy = getters.pharmaciesMap[npi] || { npi, name: 'Mail Order' }
                const ph = { npi, pharmacy, ...pharmacyData, cost }
                processPharmacyCosts(ph)
                plan.drug_cost_by_pharmacy[npi] = ph
                plan.drug_cost_by_pharmacy_list.push(ph)
            })
            plan.cms_drug_deductible = cmsDeductibleTiers(plan)
            const deductibleTiers = getters.planDrugDeductibleTiers(plan)
            const guess_drug_deductible_applicable_tiers = !deductibleTiers
            plan.drug_deductible_applicable_tiers = deductibleTiers ? deductibleTiers.tiers : []
            plan.drug_deductible_all_tiers = !!deductibleTiers?.all
            plan.drug_deductible_tiers_source = deductibleTiers ? deductibleTiers.source : 'guess'

            plan.mail_order_pharmacy = plan.drug_cost_by_pharmacy_list.filter(pc => pc.pharmacy.npi === 'mail_order')[0]
            if (!plan.mail_order_pharmacy) {
                plan.mail_order_pharmacy = plan.drug_cost_by_pharmacy_list[0]
            }
            delete plan.drug_cost_by_pharmacy['']

            plan.drug_cost_by_pharmacy_list.forEach(p => {
                if (!p.tier5s || !p.tier5s.length || p.npi === 'mail_order') {
                    return p
                }
                // // p.pharmacy_pre_tier5_override = _.cloneDeep(p)
                // subsctract the yearly_cost of each tier5 drug of this pharmacy, then add their cost of the mail_order pharmacy instead
                // // p.cost = p.cost - _.sum(p.tier5s.map(dc => dc.estimated_yearly_total)) + _.sum(p.tier5s.map(dc => plan.mail_order_pharmacy.tier5sMap[dc.package_id].estimated_yearly_total))
                // then re-process with fix=true
                processPharmacyCosts(p, { add: true /* fix: true */ })
            })

            _.each(plan.drug_info, (drugInfo, pid) => {
                drugInfo.initial_package_id = pid
                drugInfo.package = getters.packagesMap[drugInfo.ndc]
                drugInfo.alternatives = getters.findDrugAlternatives(drugInfo)
            })
            const prefer_mail_order = getters.client?.prefer_mail_order
            plan.cheapest_pharmacy = _.minBy(plan.drug_cost_by_pharmacy_list.filter(pc => prefer_mail_order ? pc : pc.pharmacy.npi !== 'mail_order'), 'cost_yearly')
            if (!plan.cheapest_pharmacy) {
                plan.cheapest_pharmacy = plan.mail_order_pharmacy
            }

            if (plan.cheapest_pharmacy) {
                const ph0 = plan.cheapest_pharmacy
                const drugCosts0 = ph0.drug_costs || []
                drugCosts0.forEach(dc => {
                    plan.drug_info[dc.ndc].is_covered = dc.is_covered
                    plan.drug_info[dc.ndc].is_not_covered = dc.is_not_covered
                    plan.drug_info[dc.ndc].is_not_covered_at = ph0
                    plan.drug_info[dc.ndc].coverage_reason = dc.coverage_reason

                    if (dc.does_count_towards_deductible) {
                        if (guess_drug_deductible_applicable_tiers && plan.drug_info[dc.ndc].tier > 0) {
                            plan.drug_deductible_applicable_tiers.push(plan.drug_info[dc.ndc].tier)
                        }
                        plan.drug_info[dc.ndc].does_count_towards_deductible = true
                        plan.drug_info[dc.ndc].does_not_count_towards_deductible = false
                    } else {
                        plan.drug_info[dc.ndc].does_count_towards_deductible = false
                        plan.drug_info[dc.ndc].does_not_count_towards_deductible = true
                    }
                })
            }
            plan.drug_deductible_applicable_tiers = _.sortBy(_.uniq(plan.drug_deductible_applicable_tiers), v => v)


            plan.estimated_yearly_cost = parseFloat(plan.annual_premium) + (plan.cheapest_pharmacy?.estimated_yearly_total || 0)
            plan.estimated_monthy_cost = plan.estimated_yearly_cost / 12

            plan.effective_date_estimated_yearly_cost = plan.effective_date_annual_premium + (plan.cheapest_pharmacy?.estimated_yearly_total || 0)

            plan.has_preferred_pharmacy = _.some(_.omit(plan.drug_data_by_pharmacy || {}, ['']), 'preferred')

            if (/HMO/i.test(plan.name)) {
                plan.has_any_out_of_network_coverage = true
            }
            if (plan.plan_type === 'mapd' || plan.plan_type === 'ma') {
                plan.has_rx = plan.drug_tiers && !/no rx|norx/i.test(plan.name) ? true : false
            } else if( plan.plan_type === 'pdp') {
                plan.has_rx = true
            }
            if (plan.name) {
                const mt = plan.name.match(/\((Regional PPO I-SNP|HMO-POS I-SNP|PPO I-SNP|HMO I-SNP|Regional PPO C-SNP|HMO-POS C-SNP|PPO C-SNP|HMO C-SNP|Regional PPO D-SNP|HMO-POS D-SNP|PPO D-SNP|HMO D-SNP|HMO POS|HMOPOS|HMO-POS|Regional PPO|PPO|HMO|MSA|PFFS)\)$/i)
                if (mt) {
                    plan.mapd_type = mt[1]
                }
            }
            if (plan.resources?.length) {
                const url = _.find(plan.resources, resource => resource.name === 'Plan Summary')?.url
                plan.document = url ? { url, type: plan.plan_type } : null
            }

            const augmentPlanBenefitCostShareNumeric = function (cost_share, cost_sharing, prop_cost_share) {
                const processCostSharingData = (costSharingItem, suffix = '') => {
                    const baseProp = `${prop_cost_share}${suffix}`
                    let percent = false
                    
                    // Handle copay values
                    if (costSharingItem.min_copay !== null && costSharingItem.max_copay !== null) {
                        if (costSharingItem.min_copay === costSharingItem.max_copay) {
                            plan[baseProp] = costSharingItem.min_copay
                        } else {
                            plan[`${baseProp}_lower`] = costSharingItem.min_copay
                            plan[`${baseProp}_upper`] = costSharingItem.max_copay
                            plan[baseProp] = costSharingItem.max_copay // Use upper value as main value
                        }
                    } else if (costSharingItem.min_copay !== null) {
                        plan[baseProp] = costSharingItem.min_copay
                        plan[`${baseProp}_lower`] = costSharingItem.min_copay
                    } else if (costSharingItem.max_copay !== null) {
                        plan[baseProp] = costSharingItem.max_copay
                        plan[`${baseProp}_upper`] = costSharingItem.max_copay
                    }
                    
                    // Handle coinsurance values (percentages)
                    if (costSharingItem.min_coinsurance !== null && costSharingItem.max_coinsurance !== null) {
                        percent = true
                        if (costSharingItem.min_coinsurance === costSharingItem.max_coinsurance) {
                            plan[baseProp] = costSharingItem.min_coinsurance
                        } else {
                            plan[`${baseProp}_lower`] = costSharingItem.min_coinsurance
                            plan[`${baseProp}_upper`] = costSharingItem.max_coinsurance
                            plan[baseProp] = costSharingItem.max_coinsurance // Use upper value as main value
                        }
                    } else if (costSharingItem.min_coinsurance !== null) {
                        percent = true
                        plan[baseProp] = costSharingItem.min_coinsurance
                        plan[`${baseProp}_lower`] = costSharingItem.min_coinsurance
                    } else if (costSharingItem.max_coinsurance !== null) {
                        percent = true
                        plan[baseProp] = costSharingItem.max_coinsurance
                        plan[`${baseProp}_upper`] = costSharingItem.max_coinsurance
                    }
                    
                    // Set fallback for main value if not set
                    if (!plan[baseProp]) {
                        plan[baseProp] = plan[`${baseProp}_upper`] || plan[`${baseProp}_lower`] || plan[baseProp]
                    }
                    
                    if (percent) {
                        plan[`${baseProp}_percent`] = true
                    }
                }

                // Use cost_sharing array if available
                if (cost_sharing && Array.isArray(cost_sharing) && cost_sharing.length > 0) {
                    // Process IN_NETWORK first (prioritize it for main values)
                    const inNetwork = cost_sharing.find(cs => cs.network_status === 'IN_NETWORK')
                    if (inNetwork) {
                        processCostSharingData(inNetwork)
                    }
                    
                    // Process OUT_OF_NETWORK with suffix
                    const outNetwork = cost_sharing.find(cs => cs.network_status === 'OUT_OF_NETWORK')
                    if (outNetwork) {
                        processCostSharingData(outNetwork, '_out_of_network')
                    }
                    
                    // Process NO_NETWORK (for emergency services, etc.)
                    const noNetwork = cost_sharing.find(cs => cs.network_status === 'NO_NETWORK')
                    if (noNetwork && !inNetwork) {
                        // Use NO_NETWORK as main values if no IN_NETWORK exists
                        processCostSharingData(noNetwork)
                    }
                    
                    // If we processed cost_sharing data, still set the note from cost_share string
                    if (cost_share) {
                        cost_share = cost_share.replace(/^(<br\s*\/>)+|(<br\s*\/>)+$/g, '');
                        const note = _.replace(cost_share, /<br( )*\/*>/i, ', ').replace(/--/, '')
                        if (note) {
                            plan[`${prop_cost_share}_note`] = note
                        }
                    }
                } else {
                    // Fallback to regex parsing (backward compatibility)
                    const match = cost_share.match(BENEFIT_COST_SHARE_RE)

                    let percent = false
                    
                    // Check for "In Network" specific patterns first
                    const inmatch = cost_share.match(IN_NETWORK_BENEFIT_COST_SHARE_RE)
                    if (inmatch) {
                        plan[prop_cost_share] = parseInt(inmatch.groups.indollars.replace(/,/g, ''))
                    } else if (match?.groups?.indollars) {
                        plan[prop_cost_share] = parseInt(match.groups.indollars.replace(/,/g, ''))
                    } else {
                        // Handle ranges and single values
                        if (match?.groups?.maxdollars) {
                            plan[prop_cost_share] = parseInt(match.groups.maxdollars.replace(/,/g, ''))
                            plan[`${prop_cost_share}_upper`] = parseInt(match.groups.maxdollars.replace(/,/g, ''))
                        } else if (match?.groups?.maxpercent) {
                            plan[prop_cost_share] = parseInt(match.groups.maxpercent.replace(/,/g, ''))
                            plan[`${prop_cost_share}_upper`] = parseInt(match.groups.maxpercent.replace(/,/g, ''))
                            percent = true
                        }
                        if (match?.groups?.mindollars) {
                            plan[prop_cost_share] = parseInt(match.groups.mindollars.replace(/,/g, ''))
                            plan[`${prop_cost_share}_lower`] = parseInt(match.groups.mindollars.replace(/,/g, ''))
                        } else if (match?.groups?.minpercent) {
                            plan[prop_cost_share] = parseInt(match.groups.minpercent.replace(/,/g, ''))
                            plan[`${prop_cost_share}_lower`] = parseInt(match.groups.minpercent.replace(/,/g, ''))
                            percent = true
                        }
                        
                        if (match?.groups?.dollars) {
                            plan[prop_cost_share] = parseInt(match.groups.dollars.replace(/,/g, ''))
                        } else if (match?.groups?.percent) {
                            plan[prop_cost_share] = parseInt(match.groups.percent.replace(/,/g, ''))
                            percent = true
                        }
                        
                        plan[prop_cost_share] = plan[`${prop_cost_share}_upper`] || plan[`${prop_cost_share}_lower`] || plan[prop_cost_share]
                    }

                    if (percent) {
                        plan[`${prop_cost_share}_percent`] = true
                    }
                    
                    // Set note from cost_share string
                    if (cost_share) {
                        cost_share = cost_share.replace(/^(<br\s*\/>)+|(<br\s*\/>)+$/g, '');
                        const note = _.replace(cost_share, /<br( )*\/*>/i, ', ').replace(/--/, '')
                        if (note) {
                            plan[`${prop_cost_share}_note`] = note
                        }
                    }
                }
            }        

            if (plan.benefits) {
                plan.services_list = []
                categories = {}
                if (_.has(plan, 'drug_deductible')) {
                    let cost_share = `${money(plan.drug_deductible)}`
                    const appliesTo = deductibleTiersText(plan.drug_deductible_applicable_tiers, plan.drug_deductible_all_tiers)
                    let cost_share_note = appliesTo ? `Applies to ${appliesTo}` : ''

                    plan.benefits['DEDUCTIBLE__DRUG_DEDUCTIBLE'] = { 
                        cost_share,
                        cost_share_note,
                    }
                    augmentPlanBenefitCostShareNumeric(cost_share, null, 'drug_deductible_cost_share')
                    plan.drug_deductible_cost_share_note = cost_share_note
                }
                _.each(plan.benefits, (value, key) => {
                    let [category, name] = key.split('__')
                    if (!name) {
                        name = category
                        category = 'GENERAL'
                    }
                    if (!category) {
                        category = 'GENERAL'
                    }
                    const originalKey = key
                    const originalCategory = category

                    if (/PREVENTIVE_CARE/.test(name)) {
                        category = 'GENERAL'
                    }
                    if (/HEALTH_PLAN_DEDUCTIBLE|OTHER_DEDUCTIBLES|DEDUCTIBLE/.test(name)) {
                        if (/HEALTH_PLAN_DEDUCTIBLE/.test(name)) {
                            name = ''
                            plan.deductible = value.cost_share
                            augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'deductible_cost_share')
                        }
                        category = 'GENERAL'
                    }
                    if (/AMBULANCE/.test(name)) {
                        category = 'EMERGENCY_CARE'
                    }
                    if (/EMERGENCY$/.test(name) && !/FOREIGN_TRAVEL_EMERGENCY/.test(name) && !/WORLDWIDE_EMERGENCY/.test(name) && !/URGENT_CARE/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'emergency_copay_cost_share')
                    }
                    if (/EMERGENCY$/.test(name) && (/FOREIGN_TRAVEL_EMERGENCY/.test(name) || /WORLDWIDE_EMERGENCY/.test(name))) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'foreign_travel_emergency_copay_cost_share')
                    }
                    if (/URGENT_CARE/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'urgent_care_copay_cost_share')
                    }
                    if (/INPATIENT_HOSPITAL/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'hospitalization_copay_cost_share')
                    }
                    if (/OUTPATIENT_HOSPITAL/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'outpatient_copay_cost_share')
                    }
                    if (/RADIOLOGY_SERVICES/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'radiology_copay_cost_share')
                    }
                    if (/LAB_SERVICES/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'bloodwork_copay_cost_share')
                    }
                    if (/OUTPATIENT_XRAYS/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'xrays_copay_cost_share')
                    }
                    if (/PHYSICAL_THERAPY/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'physical_therapy_copay_cost_share')
                    }
                    if (/PRIMARY/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'pcp_copay_cost_share')
                    }
                    if (/HEARING_AIDS/.test(name)) {
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'hearing_aids_cost_share')
                    }
                    if (/SPECIALIST/.test(name)) {
                        plan.referral_required = !!value.referral_required
                        plan.referral_not_required = !plan.referral_required
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'specialist_copay_cost_share')
                    }
                    if (/MAXIMUM_OOPC/.test(name)) {
                        category = 'OUT_OF_POCKET'
                        // "$10,000 In and Out-of-network<br />$6,700 In-network"
                        // "$7,550 In-network"
                        if (/<br \/>/.test(value.cost_share)) {
                            const parts = value.cost_share.split('<br />')
                            value.cost_share = parts[1] + '<br />' + parts[0]
                        }
                        plan.maximum_oopc = _.replace(value.cost_share, /<br( )*\/*>/i, ', ').replace(/--/, '');

                        // todo: i don't think we need this anymore
                        const parts = _.replace(value.cost_share, /<br( )*\/*>/i, '|').replace(/--/, '').split('|')
                        parts.forEach(part => {
                            const match = part.match(BENEFIT_COST_SHARE_RE)
                            if (match?.groups?.dollars && match.input) {
                                const k = match.input.slice(match.index + match[0].length).toLowerCase().replace(/-/g, '_').replace(/\s/g, '')
                                plan[`maximum_oopc_${k}`] = parseInt(match.groups.dollars.replace(/,/g, ''))
                            }
                        })
                        augmentPlanBenefitCostShareNumeric(value.cost_share, value.cost_sharing,  'maximum_oopc_cost_share')
                    }
                    if (/MENTAL_HEALTH/.test(name)) {
                        name = 'GENERAL'
                        category = 'MENTAL_HEALTH'
                    }
                    if (/INPATIENT_HOSPITAL|OUTPATIENT_HOSPITAL|SKILLED_NURSING_FACILITY/.test(name)) {
                        category = 'HOSPITAL'
                    }
                    if (/WELLNESS_PROGRAMS|OPTIONAL_SUPPLEMENTAL_BENEFITS/.test(name)) {
                        category = 'OTHER'
                    }
                    if (category === 'PREVENTIVE_DENTAL') {
                        category = 'BASIC_DENTAL'
                    }

                    const has = value => {
                        return (value && Object.keys(value).filter(k => k === 'cost_share').every(k => value[k] && !/Not covered/i.test(value[k])))
                    }

                    // yup
                    if (/VISION/.test(category)) {
                        plan.has_any_vision = !!(plan.has_any_vision || has(value))
                    }
                    if (/HEARING/.test(category)) {
                        plan.has_any_hearing = !!(plan.has_any_hearing || has(value))
                    }
                    if (/BASIC_DENTAL/.test(category)) {
                        plan.has_any_basic_dental = !!(plan.has_any_basic_dental || has(value))
                    }
                    if (/COMPREHENSIVE_DENTAL/.test(category)) {
                        plan.has_any_comprehensive_dental = !!(plan.has_any_comprehensive_dental || has(value))
                    }
                    if (/WELLNESS_PROGRAMS/.test(name)) {
                        plan.has_any_fitness = true
                    }
                    if (/DISCOUNT/.test(category)) {
                        plan.has_hd_discount = true
                    }
                    if (plan.otc || /OTC/.test(name) || /OVER_THE_COUNTER/.test(name)) {
                        plan.has_any_otc = true
                    }
                    key = `${category ? category + '__' : ''}${name}`

                    const flags = []
                    if (value.in_network_only) {
                        flags.push('In-Network only')
                    }
                    if (value.plan_limits) {
                        flags.push('Has cap')
                    }
                    if (value.referral_required) {
                        flags.push('Referral required')
                    }
                    if (value.authorization_required) {
                        flags.push('Authorization required')
                    }
                    if (value.cost_share && value.cost_share.replace) {
                        value.cost_share = value.cost_share.replace(/\n/g, ', ')
                    }
                    value.flags = flags
                    plan.benefits[key] = value

                    categories[category] = categories[category] || { services_list: [], services: {}, name: category }
                    const service = { ...value, category, name, key, flags, originalKey, originalCategory }
                    if (!service.name) {
                        service.name = service.benefit
                    }
                    plan.services_list.push(service)
                    categories[category].services_list.push(service)
                    categories[category].services[service.name] = service
                })

                plan.copays_cost_shares = (plan.emergency_copay_cost_share || 0) + (plan.pcp_copay_cost_share || 0) + (plan.specialist_copay_cost_share || 0)
                plan.copays_and_monthly = (plan.monthly_premium || 0) + plan.copays_cost_shares
                

                plan.categories = categories

                plan.categories_list = _.reduce(plan.categories, (arr, value) => {
                    value.services_list = _.sortBy(value.services_list, 'name')
                    arr.push(value)
                    return arr
                }, [])

                // meh
                const sorted = _.sortBy(plan.categories_list, function(category) {
                    const index = BENEFITS_CATEGORIES_ORDER.indexOf(category.name)
                    // no category, always on the top, index is -1 ? put it last (before drugs)
                    return category.name ? index === -1 ? Number.MAX_SAFE_INTEGER : index : -2
                });
                plan.categories_list = sorted
            }
            plan = getters.planCarriersReplace(plan)

            plan.full_id = plan.full_id || plan.full_plan_id || plan.source_id || plan.supp_type || 'UNKNOWN'
            plan.type = plan.plan_type
            plan.supp = plan.type === 'supp'
            plan.pdp = plan.type === 'pdp'
            plan.mapd = plan.type === 'mapd'
            plan.ma = plan.type === 'ma'
            plan.mapd_or_ma = plan.mapd || plan.ma
            plan.ma_or_mapd = plan.mapd || plan.ma
            plan.plan_type_label = PLAN_TYPE_LABELS[plan.plan_type]
            plan.supp_type = plan.supp_type || plan.id
            plan.supp_name = SUPP_TYPES_NAMES[plan.supp_type] || plan.supp_type

            const carrierData = {}
            plan.carrier_provider_network_link = plan.carrier_provider_network_link || carrierData?.provider_network_link

            if (plan.score_breakdown) {
                delete plan.score_breakdown['total']
                _.forEach(plan.score_breakdown, (score, key) => {
                    plan.score_breakdown[key] = parseFloat(score).toFixed(1)
                })
            }
            if (!plan.score && plan.score_breakdown) {
                plan.score = _.sum(_.values(plan.score_breakdown))
            }

            plan.yearly_deductibles = ('' + (plan.deductible || '')).split('\n').filter(d => d).map(sentence => parseInt(sentence.replace(/[^0-9.-]+/g, '')))
            plan.yearly_deductible = _.sum(plan.yearly_deductibles)

            plan.gap_start = _.get(plan, 'drug_cost_by_pharmacy_list.0.phase_information.gap_start')
            plan.catastrophic_start = _.get(plan, 'drug_cost_by_pharmacy_list.0.phase_information.catastrophic_start')

            if (plan.factors) {
                plan.factors_total = _.sum(_.toArray(plan.factors || {}))
            }

            if (plan.star_ratings) {
                plan.star_ratings = _.reduce(plan.star_ratings, (m, rating, key) => {
                    const k = key.replace(/^STAR_/, '').replace(/_RATING$/, '')
                    m[k] = rating.rating
                    return m
                }, {})
            }

            plan.carrierData = getters.planCarrierData(plan)
            plan.logo = getters.carrierData?.logo || require('~/assets/images/no-img.png')
            plan.logos = [plan.logo]
            return plan
        }
    },
    planCarrierData() {
        return plan => {
            const words = [_.snakeCase(plan.carrier), _.snakeCase(plan.parent_org)].concat(_.words(plan.carrier).map(_.snakeCase))
            let entry
            words.some(word => {
                if (carriers[word]) {
                    entry = carriers[word]
                    return true
                }
            })
            return entry
        }
    },
    isSurveyDirty(state) {
        return state.survey?.updated !== state.summary?.data?.survey_updated
    },
    isProviderInNetwork() {
        return (provider, plan) =>  { 
            return (plan?.in_network_providers || []).some(p => p == provider.npi) 
        }
    },
    isProviderOutNetwork(state, getters) {
        return (provider, plan) =>  { 
            return plan?.out_network_providers ? (plan?.out_network_providers || []).some(p => p == provider.npi) : !getters.isProviderInNetwork(provider, plan)
        }
    },
    isProviderUnsureNetwork(state, getters) {
        return (provider, plan) => !plan?.in_network_providers || !provider?.npi || (!getters.isProviderInNetwork(provider, plan) && !getters.isProviderOutNetwork(provider, plan))
    },
    providers (state) {
        return [].concat(state.survey?.providers || []).concat(state.survey?.unmatched_providers || []).filter(p => p)
    },
    providersInNetwork(state, getters) {
        return plan => getters.providers.filter(p => getters.isProviderInNetwork(p, plan))
    },
    providersOutNetwork(state, getters) {
        return plan => getters.providers.filter(p => getters.isProviderOutNetwork(p, plan))
    },
    providersUnsureNetwork(state, getters) {
        return plan => getters.providers.filter(p => getters.isProviderUnsureNetwork(p, plan))
    },
    getDrugUniquishKey() {
        return drug => `drug-${drug?.rxcui || drug?.package?.drug?.rxcui}-dosage-${drug?.dosage?.rxcui || drug?.package?.dosage?.rxcui}-package-${drug?.package?.ndc}-quantity-${drug?.quantity}-frequency-${drug?.frequency}-excluded-${drug?.excluded || false}`
    },
    isPlanInSummary (state) {
        return plan => (state.summary?.data?.plans || {})[plan.id]
    },
    isUserGroupPlanInSummary (state) {
        return plan => (state.summary?.data?.user_group_plans || {})[plan.id]
    },
    // An opportunity counts only for its own year: Salesforce keeps one product
    // per plan code and re-points it to each new year's plan id (2026-10-02),
    // so a client's 2026 enrollment marked the 2027 plan "Enrollment started".
    // One still in progress also counts for the next year: an enrollment
    // starting Nov/Dec 2026 sits on a product already pointing at 2027.
    isPlanInOpportunities (state, getters) {
        const inProgress = opp => !/^(enrolled|closed)/i.test(opp.StageName || '')
        return (plan) => {
            const year = Number(plan?.year || getters.year) || null
            return _.find(state.opportunities, (opp) => {
                if (!opp || !plan?.id) { return false }
                if (_.get(opp, 'LineItem.Product2.informatics_plan_id__c') != plan.id && _.get(opp, 'informatics_plan_id__c') != plan.id) { return false }
                if (!opp.Effective_Date__c || !year) { return true }
                const effective = moment(opp.Effective_Date__c).year()
                return effective === year || (effective === year - 1 && inProgress(opp))
            })
        }
    },
    aepOpportunities (state, getters) {
        return state.opportunities.filter(opp => opp.Enrollment_Period__c === 'AEP' && (!opp.Effective_Date__c || moment(opp.Effective_Date__c).year() === getters.nextYear || moment(opp.Effective_Date__c).year() === getters.year))
    },
    excludedDrugs (state) {
        return (state?.survey?.drugs || []).filter(d => d.excluded)
    },
    findDrugAlternatives (state) {
        return (drug) => {
            const label = _.toLower(drug?.label || drug?.name || drug?.package?.label || drug || '')
            if (!label) {
                console.warn('Was not able to find a label for drug:', drug)
                return []
            }
            const words = label.split(' ')
            if (state.drugsAlternativesMap[label]) {
                return state.drugsAlternativesMap[label].slice(1)
            }
            let alternatives = []
            words.some((word) => {
                if (state.drugsAlternativesMap[word]) {
                    alternatives = state.drugsAlternativesMap[word].slice(1)
                    return true
                }
                return false
            })
            return alternatives
        }
    },
    paymentMethods () {
        return [
            { value: 'QUARTERLY_MAIL', text: 'Quarterly Mail' },
            { value: 'MONTHLY_SSD', text: 'Monthly Social Security Deduction' },
            { value: 'MONTHLY_EFT', text: 'Monthly EFT (Electronic Funds Transfer)' },
            { value: 'MONTHLY_MAIL', text: 'Monthly Mail' },
            { value: 'UNDECIDED', text: 'Undecided' },
            // { value: 'CREDIT_CARD', text: 'Credit Card' }
        ]
    },
    paymentMethodsMap (state, getters) {
        return _.keyBy(getters.paymentMethods, 'value')
    },
    paymentMethodsPerProduct(state, getters) {
        const all = getters.paymentMethodsMap

        const methods = {
            part_a: [
                // todo: @luke [no-question] we don't ask ss_retirement_benefits
                state.survey?.ss_retirement_benefits ? all.MONTHLY_SSD : all.QUARTERLY_MAIL,
                all.UNDECIDED
            ],
            part_b: [
                state.survey?.ss_retirement_benefits ? all.MONTHLY_SSD : all.QUARTERLY_MAIL,
                all.UNDECIDED,
            ],
            irmaa_part_b: [
                state.survey?.ss_retirement_benefits ? all.MONTHLY_SSD : all.QUARTERLY_MAIL,
                all.UNDECIDED,
            ],
            irmaa_part_d: [
                all.MONTHLY_EFT,
                all.MONTHLY_MAIL,
                all.UNDECIDED,
            ],
            supp: [
                all.MONTHLY_EFT,
                all.MONTHLY_MAIL,
                all.UNDECIDED,
            ],
            pdp: getters.survey?.collecting_ssi
                ? [
                    all.MONTHLY_EFT,
                    all.MONTHLY_SSD,
                    all.MONTHLY_MAIL,
                    all.UNDECIDED,
                ]
                : [
                    all.MONTHLY_EFT,
                    // all.CREDIT_CARD,
                    all.MONTHLY_MAIL,
                    all.UNDECIDED,
                ],
            supp_pdp: [
                all.MONTHLY_EFT,
                all.MONTHLY_MAIL,
                all.UNDECIDED,
            ],
            mapd: getters.survey?.collecting_ssi
                ? [
                    all.MONTHLY_EFT,
                    all.MONTHLY_SSD,
                    all.MONTHLY_MAIL,
                    all.UNDECIDED,
                ]
                : [
                    all.MONTHLY_EFT,
                    // all.CREDIT_CARD,
                    all.MONTHLY_MAIL,
                    all.UNDECIDED,
                ],
            gp: [
                all.MONTHLY_EFT,
            ],
            dental: [
                all.MONTHLY_EFT,
                all.MONTHLY_MAIL,
                all.UNDECIDED,
            ],
            vision: [
                all.MONTHLY_EFT,
                all.MONTHLY_MAIL,
                all.UNDECIDED,
            ],
            tricare: [],
            champva: [],
        }
        return methods
    },
    paymentMethodsPerProductText (state, getters) {
        return _.map(getters.paymentMethodsPerProduct)
    },
    quarters () {
        return [
            { text: 'Yes', value: 'QUARTERS_40' },
            { text: "No, but I've paid for at least 7 years and 6 months (30 quarters)", value: 'QUARTERS_30_39' },
            { text: "No, I've paid for 7 years and 3 months or less (29 quarters or less)", value: 'QUARTERS_LT_30' },
            { text: 'Not sure', value: null },
        ]
    },
    filings () {
        return [
            { text: 'Filing Single', value: 'SINGLE' },
            { text: 'Filing Jointly', value: 'MARRIED_JOINT' },
            { text: 'Married filing separately', value: 'MARRIED_SEPARATELY' },
        ]
    },
    clientRelationships () {
        return [
            { text: 'Spouse / Partner', value: 'SPOUSE' },
            { text: 'My Parent', value: 'PARENT' },
            { text: 'My Child', value: 'CHILD' },
            { text: 'My Friend', value: 'FRIEND' },
            { text: 'Other', value: 'OTHER' },
        ]
    },
    groupRelationships () {
        return [
            { text: 'Employee', value: 'EMPLOYEE' },
            { text: 'Retiree', value: 'RETIREE' },
            { text: 'Spouse of employee', value: 'EMPLOYEE_SPOUSE' },
            { text: 'Dependant of employee', value: 'EMPLOYEE_DEPENDENT' },
            { text: 'Parent of employee', value: 'EMPLOYEE_PARENT' },
            { text: 'Other relative of employee', value: 'OTHER_RELATIVE' },
            { text: 'Acquaintance of employee', value: 'ACQUAINTANCE_OF_EMPLOYEE' },
            { text: 'Referred by client', value: 'REFERRED_BY_CLIENT' },
            { text: 'None of the above / Other', value: 'OTHER' },
        ]
    },
    genders () {
        return [
            { text: 'Female', value: 'F' },
            { text: 'Male', value: 'M' },
        ]
    },
    yesnos () {
        return [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
        ]
    },
    tobaccos () {
        return [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
        ]
    },
    enrollmentPeriods () {
        return [
            { text: 'Initial Enrollment Period (IEP)', value: 'IEP' },
            { text: 'Annual Open Enrollment Period (AEP)', value: 'AEP' },
            { text: 'Open Enrollment Period (OEP)', value: 'OEP' },
            { text: 'General Enrollment Period (GEP)', value: 'GEP' },
            { text: 'Special Enrollment Period (SEP)/Leaving Employment', value: 'SEP_LEC' },
            { text: 'Special Enrollment Period (SEP)/Moving', value: 'SEP_MOVING' },
            { text: 'Special Enrollment Period (SEP)/Lost Coverage', value: 'SEP_LOST_COVERAGE' },
            { text: 'Special Enrollment Period (SEP)/Lost Medicaid', value: 'SEP_LOST_MEDICAID' },
            { text: '5-Star', value: '5-Star' },
            { text: 'Guaranteed Issue', value: 'GUARANTEED_ISSUE' },

        ]
    },
    reasons () {
        return [
            { text: 'Left employer coverage', value: 'LEFT_EMPLOYER_COVERAGE' },
            { text: 'Switch MAPD', value: 'SWITCH_MAPD' },
            { text: 'Moved', value: 'MOVED' },
            { text: 'Released from incarceration', value: 'INCARCERATION_RELEASED' },
            { text: 'Expat returned', value: 'EXPAT_RETURNED' },
            { text: 'Extra help qualified', value: 'EXTRA_HELP_QUALIFIED' },
            { text: 'Both Medicare & Medicaid', value: 'BOTH_MEDICARE_MEDICAID' },
            { text: 'Moved LTC', value: 'MOVED_LTC' },
            { text: 'Left PAGE', value: 'LEFT_PACE' },
            { text: 'Lost drug coverage', value: 'LOST_DRUG_COVERAGE' },
            { text: 'Pharmacy assistance', value: 'PHARMACY_ASSISTANCE' },
            { text: 'ICHRA', value: 'ICHRA' },
        ]
    },
    distances () {
        return [
            { text: '10 miles', value: 10 },
            { text: '25 miles', value: 25 },
            { text: '50 miles', value: 50 },
            { text: '100 miles', value: 100 },
        ]
    },
    frequencies () {
        return [
            { text: 'Every month', value: 'FREQUENCY_30_DAYS' },
            { text: 'Every 2 months', value: 'FREQUENCY_60_DAYS' },
            { text: 'Every 3 months', value: 'FREQUENCY_90_DAYS' },
            { text: 'Every 6 months', value: 'FREQUENCY_180_DAYS' },
            { text: 'Every 12 months', value: 'FREQUENCY_360_DAYS' },
        ]
    },
    travelTargets () {
        return [
            { text: 'Domestic', value: 'DOMESTIC' },
            { text: 'International', value: 'INTERNATIONAL' },
            { text: 'Both', value: 'BOTH' },
        ]
    },
    preferredPlanClasses () {
        return [
            { text: 'Low Premium & High Copays', value: 'LOW_PREMIUM_HIGH_COPAYS' },
            { text: 'High Premium & Low Out of Pocket Costs', value: 'HIGH_PREMIUM_LOW_OOP' },
            { text: 'No Preference', value: 'NONE' },
        ]
    },
    // #727: the dropdown order Dien asked for. Friend / Doctor / Social Media are
    // no longer offered for new bookings but stay here flagged `legacy` so an
    // existing record that carries one still shows its label (sourceTypesMap).
    sourceTypes (state, getters) {
        return [
            { text: 'Employer', value: 'EMPLOYER' },
            { text: 'Family Member', value: 'FAMILY_MEMBER' },
            getters.canAgent ? { text: 'Direct Primary Care', value: 'DIRECT_PRIMARY_CARE' } : null,
            { text: 'Financial Advisor', value: 'FINANCIAL_ADVISOR' },
            { text: 'Other', value: 'OTHER' },
            getters.canAgent ? { text: 'Doctor\'s Choice Internal', value: 'DOCTOR_CHOICE_INTERNAL' } : null,
            { text: 'Friend', value: 'FRIEND', legacy: true },
            { text: 'Doctor', value: 'DOCTOR', legacy: true },
            { text: 'Social Media', value: 'SOCIAL_MEDIA', legacy: true },
        ].filter(v => v)
    },
    sourceTypesMap (state, getters) {
        return _.keyBy(getters.sourceTypes, 'value')
    },
    ichraTpas (state, getters) {
        return [
            { text: 'BenefitBay', value: 'BenefitBay' },
            { text: 'SureCo', value: 'SureCo' },
        ].filter(v => v)
    },
    medicareParts () {
        // `description` is an optional sub-label used by the v2 scheduler cards;
        // b-form-select consumers (demographics, ClientForm) ignore it. The
        // `value`s are the MedicareParts enum and are synced with Salesforce.
        return [
            {
                value: 'A',
                text: 'I only have part A',
              },
              {
                value: 'AB',
                text: 'I have both Medicare Part A and Part B',
              },
              {
                value: 'NONE',
                text: 'I do not have Medicare Part A or Part B',
              },
              {
                value: 'UNSURE',
                text: 'I\'m not sure',
              }
        ]
    },
    householdDiscounts () {
        return [
            { text: 'No discount', value: null },
            { text: 'Spouse', value: 'SPOUSE' },
            { text: 'Roommate', value: 'ROOMMATE' },
        ]
    },
    suppRateTypes () {
        return [
            { title: 'Attained Age Pricing', text: 'Premiums are low for younger buyers, but go up as you get older and can eventually become the most expensive.', value: 'MEDIGAP_RATE_TYPE_ATTAINED_AGE' },
            { title: 'Community Pricing', text: 'Premiums are the same no matter how old you are. Premiums may go up because of inflation and other factors.', value: 'MEDIGAP_RATE_TYPE_COMMUNITY_RATED' },
            { title: 'Issue Age Pricing', text: 'Premiums are low for younger buyers and won\'t change as you get older.', value: 'MEDIGAP_RATE_TYPE_ISSUE_AGE' },
        ]
    },
    suppRateTypesMap (state, getters) {
        return _.keyBy(getters.suppRateTypes, 'value')
    },
    planTypes () {
        return [
            { text: 'Any', value: null },
            { text: 'Supplemental (SUPP)', value: 'SUPP' },
            { text: 'Drug (PDP)', value: 'PDP' },
            { text: 'Advantage (MAPD)', value: 'MAPD' },
            { text: 'N/A', value: 'NA' },
        ]
    },
    abPartsOptions () {
        return [
            { value: 'A', text: 'Part A Only' },
            { value: 'AB', text: 'Parts A and B' },
            { value: null, text: 'None' }
        ]
    },
    usersRelationshipTypes (state, getters) {
        return [
            { value: "SPOUSE", text: "Spouse" },
            { value: "PARENT", text: "Parent" },
            { value: "CHILD", text: "Child" },
            { value: "FRIEND", text: "Friend" },
            { value: "OTHER", text: "Other" }
        ]
    },
    coverageUrgencies () {
        return [
            { value: 'ASAP', text: 'As soon as possible' },
            { value: 'MONTHS', text: 'Sometime in the next several months' },
            { value: 'EXACT', text: 'I have a specific start date in mind' },
            { value: 'UNSURE', text: 'I am not sure yet' },
        ]
    },
    months () {
        return [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
        ]
    },
    dowShort () {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    },
    dowFull () {
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    },
    // Scope-of-Appointment products shown in the v2 scheduler.
    // NOTE (Salesforce sync): each `id` is the product key persisted in
    // SignedSoaForm.products / product_answers on the backend — do not rename
    // an id without updating the backend + Salesforce mapping.
    // Scope-of-Appointment products shown in the v2 scheduler.
    // NOTE (Salesforce sync): each `value` is the product key persisted in
    // SignedSoaForm.products / product_answers on the backend — do not rename a
    // value without updating the backend + Salesforce mapping. `more` is the
    // expandable "What is this?" explainer (wizard-only).
    soaProducts () {
        return [
            {
                value: 'mapd',
                icon: 'heart',
                title: 'Medicare Advantage (Part C)',
                description: 'One plan that bundles your hospital care, doctor visits, and usually your prescription drugs.',
                more: 'With a Medicare Advantage plan, a private insurer manages your Medicare benefits in one plan. Many include extras like prescription drugs, dental, or a fitness membership, often with a low or $0 monthly premium.',
            },
            {
                value: 'pdp',
                icon: 'prescription-bottle',
                title: 'Prescription Drug Plans (Part D)',
                description: 'Coverage that lowers what you pay for the medications you take.',
                more: 'A Part D plan helps cover the cost of prescription medications. Each plan has its own list of covered drugs, so the right plan depends on the medications you take.',
            },
            {
                value: 'supp',
                icon: 'shield-check',
                title: 'Medicare Supplement (Medigap)',
                description: 'Works alongside Original Medicare to help pay deductibles and copays.',
                more: 'A Medigap plan pays some or all of the out-of-pocket costs Original Medicare leaves you, like deductibles, copays, and coinsurance, so your costs are more predictable.',
            },
            {
                value: 'dental_vision_hearing',
                icon: 'smile',
                title: 'Dental, Vision, and Hearing',
                description: "Routine dental, eye, and hearing care, which Medicare usually doesn't include.",
                more: 'Original Medicare generally doesn’t cover routine dental, vision, or hearing care. A standalone plan can help with cleanings, eye exams, glasses, and hearing aids.',
            },
        ]
    },
    // "How are you connected to Doctor's Choice?" tiles in the v2 scheduler.
    // `value` is the wizard's internal connection key (employer/advisor/family/other).
    // NOTE (Salesforce sync): `sourceType` is the SourceType enum stored on the
    // client (source_type) and synced with Salesforce — keep these in step with
    // the `sourceTypes` getter / backend SourceType enum.
    connections () {
        return [
            { value: 'employer', icon: 'building', title: 'My employer', description: 'Benefit through work', sourceType: 'EMPLOYER' },
            { value: 'advisor', icon: 'briefcase', title: 'Financial advisor', description: 'Referred by my advisor', sourceType: 'FINANCIAL_ADVISOR' },
            { value: 'family', icon: 'users', title: 'Family member', description: 'Their employer participates', sourceType: 'EMPLOYER' },
            // #727
            { value: 'dpc', icon: 'stethoscope', title: 'Direct Primary Care', description: 'Referred by my doctor', sourceType: 'DIRECT_PRIMARY_CARE' },
            { value: 'other', icon: 'info-circle', title: 'None of the Above / Not Sure', description: 'Something else', sourceType: 'OTHER', full: true },
        ]
    },
    preMedicareCoverages () {
        return [
            {
                value: 'EMPLOYER',
                text: 'Insurance from my/my spouse/family member employer'
            },
            {
                value: 'INDIVIDUAL',
                text: 'Individual'
            },
            {
                value: 'COBRA',
                text: 'COBRA'
            },
            {
                value: 'MEDICAID',
                text: 'Medicaid'
            },
            {
                value: 'OTHER',
                text: 'Other insurance, including marketplace'
            },
            {
                value: 'NONE',
                text: 'I don\'t have any insurance'
            }
        ]
    },
    postMedicareCoverages () {
        return [
            {
                value: 'SUPP_PDP',
                text: 'Supplement Plan (Medigap) + Prescription Drug Plan (Part D)'
            },
            {
                value: 'MAPD',
                text: 'Medicare Advantage Plan (Part C)'
            },
            {
                value: 'SUPP',
                text: 'Only Supplement Plan (Medigap)'
            },
            {
                value: 'PDP',
                text: 'Only Prescription Plan (Part D)'
            },
            {
                value: 'NONE',
                text: 'None, just original Medicare (Part A & B)'
            },
            {
                value: 'UNSURE',
                text: 'I\'m not sure'
            },
        ]
    },
    opportunitiesTypes () {
        return [
            {
                value: 'New Business - New to Medicare',
                text: 'New Business - New to Medicare'
            },
            {
                value: 'New Business - Renewal Enrollment',
                text: 'New Business - Renewal Enrollment'
            },
            {
                value: 'Existing Business - Renewal Enrollment',
                text: 'Existing Business - Renewal Enrollment'
            },
        ]
    },
    alertsIconsVariantsMap () {
        return {
            warning: '⚠️',
            danger: '🔴',
            error: '🔴',
            success: '🟢',
            info: '🔵',

        }
    },
    alertsFaIconsVariantsMap () {
        return {
            success: 'thumbs-up',
            info: 'info',
            warning: 'exclamation',
            danger: 'exclamation-circle',
        }
    },
    alertsTitlesVariantsMap () {
        return {
            danger: 'Error',
        }
    },
    usStates (state) {
        return (_.isArray(state.usStates) ? state.usStates || [] : []).map(v => ({ text: v, value: v }))
    },
    option(state, getters) {
        return (getter, value, fallback) => {
            return _.find(getters[getter] || [], option => option.value === value)?.text || fallback
        }
    },
    activityChoices (state) {
        return state.activityChoices.map(a => ({ text: a, value: a }))
    },
    carriersProvidersSearchLinks () {
        return CARRIER_PROVIDERS_SEARCH_LINKS
    },
    getScheduleHelpText (state, getters) {
        return (key) => {
            if (_.isString(SCHEDULE_HELP_TEXT[key])) {
                return SCHEDULE_HELP_TEXT[key]
            }
            if (_.isFunction(SCHEDULE_HELP_TEXT[key])) {
                return SCHEDULE_HELP_TEXT[key](state, getters)
            }
        }
    },
    scheduleHelpText (state) {
        return SCHEDULE_HELP_TEXT
    },
    scheduleHelpTextEmailOptIn (state, getters) {
        return getters.getScheduleHelpText('email_opt_in')
    },
    commonMixinComputed () {
        return CommonMixin.computed
    },
    cardMixinComputed () {
        return { ...CardMixin.mixins[0].computed, ...CardMixin.computed }
    },
    commonMixinKeys (state, getters) {
        return Object.keys(getters.commonMixinComputed)
    },
    cardMixinKeys (state, getters) {
        return Object.keys(getters.cardMixinComputed)
    },
    surveyKeys (state, getters) {
        return state.survey ? Object.keys(state.survey).filter(k => !getters.commonMixinComputed.hasOwnProperty(k) && !getters.cardMixinComputed.hasOwnProperty(k)) : []
    },
    variables (state, getters) {
        return _.uniq(getters.surveyKeys.concat(getters.cardMixinKeys).concat(getters.commonMixinKeys))
    },
    variablesOptions (state, getters) {
        return getters.variables.map(k => ({ label: k, raw: `{{ ${k} }}`, value: getters.window.$root.vueTemplateToHtml(`{{ ${k} }}`) }))
    },
    variablesComputed (state, getters) {
        return {
            ...getters.commonMixinComputed,
            ...getters.cardMixinComputed,
        }
    },
    variablesData (state, getters) {
        const d = {...(state.survey || {})}
        getters.cardMixinKeys.forEach(k => {
            delete d[k]
        })
        getters.commonMixinKeys.forEach(k => {
            delete d[k]
        })
        return d
    },
    possibleNorxSituations () {
        return ['va', 'champva', 'tricare']
    },
    norxSituations (state, getters) {
        return getters.possibleNorxSituations?.filter(situation => state.survey && state.survey[situation])
    },
    hasNorxSituations (state, getters) {
        return getters.norxSituations?.length
    },
}
